// src/components/dashboard/cases/newCase/case_id/caseTable/STLViewer.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils";
import "./STLViewer.css";

import {
  SMOOTH_VERTEX_LIMIT, HEAVY_TRIANGLE_COUNT,
  ROTATE_SPEED, ZOOM_SPEED, PAN_FEEL, DAMPING,
  INITIAL_FIT_FACTOR, PAN_EDGE_FACTOR, MAX_ZOOM_OUT_FACTOR, ZOOM_OUT_BASE_FACTOR,
  OPACITY_MIN, OPACITY_MAX,
  CLIP_PLANE_SIZE_MULT_DEFAULT, CLIP_PLANE_SIZE_MULT_MIN, CLIP_PLANE_SIZE_MULT_MAX,
  CLIP_PLANE_COLOR, CLIP_PLANE_EDGE, CLIP_PLANE_EDGE_INVERTED, CLIP_PLANE_OPACITY,
  CLIP_OFFSET_RANGE, CLIP_HANDLE_RED, CLIP_HANDLE_BLUE, CLIP_HANDLE_GREEN,
  CLIP_HANDLE_PURPLE, CLIP_ARROW_COLOR, CLIP_OUTLINE_COLOR, CLIP_MAX_SEGMENTS,
  MEASURE_COLOR, MEASURE_ACCENT, MEASURE_HALO_COLOR, MEASURE_HALO_PX, MEASURE_UNIT,
  formatMm, MEASURE_TUBE_PX, MEASURE_CONE_RADIUS_PX, MEASURE_CONE_LENGTH_PX,
  MEASURE_DOT_PX, MEASURE_HIT_PX,
  DRAW_LINE_COLOR, DRAW_LINE_HALO_COLOR, DRAW_LINE_PX, DRAW_LINE_HALO_PX,
  DRAW_DOT_PX, DRAW_DOT_HALO_PX,
  MULTI_MODEL_GAP_FACTOR, CONNECT_SHARED_SPACE_MODELS,
  CONNECT_TOLERANCE_FACTOR, CONNECT_MIN_SPREAD_FACTOR,
  MODEL_SELECTED_BASE_COLOR, MODEL_SELECTED_EMISSIVE,
  MODEL_SELECTED_EMISSIVE_INTENSITY, MODEL_SELECTED_METALNESS,
  MODEL_SELECTED_ROUGHNESS, MODEL_ROTATE_SPEED,
  DEFAULT_MODEL_STATE, ROT_HANDLES, VIEW_DIRECTIONS,
} from "./constants";

import { isPlyUrl, computeLoopsArea, closestParamOnLine } from "./utils/geometry";
import {
  loadLayout, saveLayout, DOCK_EDGES, DOCK_POS,
  snapDock, swallowNextContextMenu,
} from "./utils/dock";

import Sidebar from "./components/Sidebar";
import OpacityPanel from "./components/OpacityPanel";
import FilesPanel from "./components/FilesPanel";
import HelpOverlay from "./components/HelpOverlay";
import DrawHint from "./components/DrawHint";
import CrossSectionPanel from "./components/CrossSectionPanel";
import CrossSectionViewer2D from "./components/CrossSectionViewer2D";
// ✅ NEW: screenshot -> draw -> add to note
import ScreenshotEditor, {
  CameraIcon,
  canAddScreenshotNote,
  sendScreenshotToNote,
  getViewerBackground,
  drawMeasureLabelOnto,
  STL_CAPTURE_REQUEST_EVENT,
} from "./components/ScreenshotEditor";

import { useViewerState } from "./hooks/useViewerState";
import { useCrossSection2D } from "./hooks/useCrossSection2D";
import { loadViewerState, saveViewerState } from "./stlViewerSession";

const XS_SCALE_MIN = 0.6;
const XS_SCALE_MAX = 2;
const XS_SCALE_STEP = 0.2;

const MERGE_VERTICES_MAX_TRIANGLES = 50000;

// ---- Bite / occlusion check ----
const BITE_GRID = 256;          // height-map resolution
const BITE_MIN_SAMPLES = 2;     // min vertices per cell (ignores stray noise)
const BITE_CONTACT_MM = 0.1;    // gap below this = "contact" (red)
const BITE_MAX_MM = 2;          // gap at/above this = blue, beyond = grey

// ✅ NEW: screenshot button + area-select overlay styles
const SHOT_BTN_CSS = `
  /* ✅ FIX: top-centre of the viewer (the corners are used by the 2D section
     panel, opacity panel, help bar and 2D viewer) + above every panel */
  .stl-shot-btn {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 24;
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.55);
    background: linear-gradient(135deg, rgba(0, 74, 173, 0.95) 0%, rgba(10, 99, 216, 0.95) 100%);
    color: #fff;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(0, 30, 90, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.25);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .stl-shot-btn:hover { transform: translateX(-50%) translateY(-1px) scale(1.05); box-shadow: 0 8px 22px rgba(0, 30, 90, 0.45); }
  .stl-shot-btn:active { transform: translateX(-50%) scale(0.95); }
  /* while the "draw a section line" hint is shown at the top, move down */
  .stl-shot-btn.is-low { top: 64px; }

  .stl-shot-flash {
    position: absolute;
    inset: 0;
    z-index: 30;
    background: #fff;
    pointer-events: none;
    animation: stl-shot-flash 0.35s ease-out forwards;
  }
  @keyframes stl-shot-flash { from { opacity: 0.75; } to { opacity: 0; } }

  /* area selection */
  .stl-cap-overlay {
    position: absolute;
    inset: 0;
    z-index: 25;
    cursor: crosshair;
    touch-action: none;
    user-select: none;
    background: rgba(8, 20, 45, 0.38);
    overflow: hidden;
  }
  .stl-cap-overlay.has-sel { background: transparent; }
  .stl-cap-sel {
    position: absolute;
    border: 2px solid #fff;
    outline: 1px dashed rgba(0, 74, 173, 0.9);
    outline-offset: -4px;
    box-shadow: 0 0 0 9999px rgba(8, 20, 45, 0.45);
    pointer-events: none;
  }
  .stl-cap-size {
    position: absolute;
    left: 0;
    top: -26px;
    padding: 2px 8px;
    border-radius: 6px;
    background: #004aad;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .stl-cap-bar {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 6px 6px 12px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.9);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    cursor: default;
    white-space: nowrap;
    max-width: calc(100% - 24px);
  }
  .stl-cap-hint { display: inline-flex; align-items: center; gap: 6px; overflow: hidden; text-overflow: ellipsis; }
  .stl-cap-btn {
    border: none;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }
  .stl-cap-btn.primary { background: #fff; color: #004aad; }
  .stl-cap-btn.primary:hover { background: #eef4fb; }
  .stl-cap-btn.ghost { background: rgba(255, 255, 255, 0.14); color: #fff; }
  .stl-cap-btn.ghost:hover { background: rgba(255, 255, 255, 0.26); }

  @media (max-width: 640px) {
    .stl-shot-btn { top: 8px; width: 34px; height: 34px; }
    .stl-shot-btn.is-low { top: 56px; }
    .stl-cap-bar { top: 8px; font-size: 11px; gap: 6px; padding: 5px 5px 5px 10px; }
    .stl-cap-hint-long { display: none; }
    .stl-cap-btn { padding: 5px 9px; font-size: 11px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .stl-shot-flash { animation: none; opacity: 0; }
  }
`;

const STLViewer = ({
  fileUrl,
  fileUrls,
  size = "small",
  shouldLoad = true,
  onLoad,
  onError,
  onProgress,
  // ✅ NEW: false = hide the in-viewer screenshot button (e.g. when the
  // page shows its own button in the header and uses requestStlScreenshot())
  showScreenshotButton = true,
}) => {
  const isSmall = size === "small";
  const isMulti = Array.isArray(fileUrls) && fileUrls.length > 0;

  const mountRef = useRef(null);
  const viewApiRef = useRef(null);
  const materialRef = useRef(null);
  const controlsRef = useRef(null);
  const meshRef = useRef(null);
  const meshesRef = useRef([]);
  const autoRotateRef = useRef(false);
  const wireframeRef = useRef(false);
  const opacityRef = useRef(1);

  const biteApiRef = useRef(null);
  const [biteMode, setBiteMode] = useState(false);
  const [biteInfo, setBiteInfo] = useState(null);

  // ✅ NEW: screenshot state
  const [shot, setShot] = useState(null);           // data URL of the capture
  const [shotCanNote, setShotCanNote] = useState(false);
  const [shotFlash, setShotFlash] = useState(0);
  // ✅ NEW: choose what to capture - drag an area or take the whole view
  const [captureMode, setCaptureMode] = useState(false);
  const [captureSel, setCaptureSel] = useState(null); // { x, y, w, h } in container px
  const captureDragRef = useRef(null);

  const {
    wireframe, setWireframe,
    autoRotate, setAutoRotate,
    opacity, setOpacity,
    showOpacity, setShowOpacity,
    expanded, setExpanded,
    ready, setReady,
    sidebarHidden, setSidebarHidden,
    showCrossSection, setShowCrossSection,
    clipEnabled, setClipEnabled,
    clipInverted, setClipInverted,
    planeOffset, setPlaneOffset,
    crossSectionOutline, setCrossSectionOutline,
    planeSizeMult, setPlaneSizeMult,
    measureMode, setMeasureMode,
    sectionDrawMode, setSectionDrawMode,
    xsMeasureVersion, setXsMeasureVersion,
    modelStates, setModelStates,
    filesPanelOpen, setFilesPanelOpen,
    getModelState, updateModelState,
  } = useViewerState({ isSmall, isMulti, fileUrl, fileUrls });

  const toggleModelDetached = (i) => {
    const next = !getModelState(i).detached;
    if (viewApiRef.current && viewApiRef.current.setDetached) {
      viewApiRef.current.setDetached(i, next);
    }
    updateModelState(i, { detached: next });
  };

  const showAllModels = () =>
    setModelStates((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        next[k] = { ...DEFAULT_MODEL_STATE, ...prev[k], visible: true };
      });
      return next;
    });

  const [xsScale, setXsScale] = useState(1);
  const changeXsScale = (delta) =>
    setXsScale((v) =>
      Math.min(XS_SCALE_MAX, Math.max(XS_SCALE_MIN, +(v + delta).toFixed(2)))
    );

  const layoutInit = useMemo(() => (isSmall ? {} : loadLayout()), [isSmall]);
  const containerRef = useRef(null);
  const dockRef = useRef(null);
  const xsViewerRef = useRef(null);

  const [dockEdge, setDockEdge] = useState(() =>
    DOCK_EDGES.includes(layoutInit.dockEdge) ? layoutInit.dockEdge : "right"
  );
  const [dockPos, setDockPos] = useState(() =>
    DOCK_POS.includes(layoutInit.dockPos) ? layoutInit.dockPos : "center"
  );
  const [dockDragging, setDockDragging] = useState(false);

  const [xsPos, setXsPos] = useState(() =>
    layoutInit.xsPos &&
    typeof layoutInit.xsPos.fx === "number" &&
    typeof layoutInit.xsPos.fy === "number"
      ? layoutInit.xsPos
      : null
  );

  const measureActiveRef = useRef(false);
  const measureApiRef = useRef(null);
  const measureLabelRef = useRef(null);

  const callbacksRef = useRef({});
  callbacksRef.current = { onLoad, onError, onProgress };
  autoRotateRef.current = autoRotate;
  wireframeRef.current = wireframe;
  opacityRef.current = opacity / 100;
  measureActiveRef.current = measureMode;

  const clipEnabledRef = useRef(false);
  const clipInvertedRef = useRef(false);
  const planeOffsetRef = useRef(0);
  const planeSizeMultRef = useRef(CLIP_PLANE_SIZE_MULT_DEFAULT);
  const clipApiRef = useRef(null);
  const crossSectionCanvasRef = useRef(null);

  const sectionActiveRef = useRef(false);
  const sectionDrawModeRef = useRef(false);
  const drawApiRef = useRef(null);

  clipEnabledRef.current = clipEnabled;
  clipInvertedRef.current = clipInverted;
  planeOffsetRef.current = planeOffset;
  planeSizeMultRef.current = planeSizeMult;
  sectionActiveRef.current = showCrossSection;
  sectionDrawModeRef.current = sectionDrawMode;

  const { xsMeasurePointsRef, xsViewRef } = useCrossSection2D({
    crossSectionOutline,
    xsMeasureVersion,
    crossSectionCanvasRef,
    setXsMeasureVersion,
  });

  // ==== MAIN THREE.JS EFFECT ====
  useEffect(() => {
    if (!shouldLoad || !mountRef.current) return undefined;
    if (!isMulti && !fileUrl) return undefined;
    if (isMulti && fileUrls.length === 0) return undefined;

    const mount = mountRef.current;
    let disposed = false;
    let frameId = null;
    let resizeObserver = null;
    let radius = 1;
    let fitDistance = 100;
    let maxDistanceLimit = 1000;
    let needsRender = true;
    let prevTime = 0;
    let isHeavy = false;
    let restoreTimer = null;

    const fullPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const tmpVec = new THREE.Vector3();
    const camRight = new THREE.Vector3();
    const camUp = new THREE.Vector3();

    const visMeshes = () => meshesRef.current.filter((m) => m.visible);

    setReady(false);

    const scene = new THREE.Scene();
    if (isSmall) scene.background = new THREE.Color("#000000");

    const getSize = () => {
      if (isSmall) return { w: 160, h: 150 };
      return {
        w: Math.max(mount.clientWidth, 200),
        h: Math.max(mount.clientHeight, 200),
      };
    };
    const { w: initW, h: initH } = getSize();

    const camera = new THREE.PerspectiveCamera(45, initW / initH, 0.1, 5000);
    camera.position.set(0, 0, 100);
    camera.up.set(0, 1, 0);
    scene.add(camera);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: !isSmall,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(fullPixelRatio);
    renderer.setSize(initW, initH, false);
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.outline = "none";
    renderer.domElement.style.touchAction = "none";
    mount.appendChild(renderer.domElement);

    renderer.localClippingEnabled = true;

    const controls = new TrackballControls(camera, renderer.domElement);
    controls.rotateSpeed = ROTATE_SPEED;
    controls.zoomSpeed = ZOOM_SPEED;

    const updatePanSpeed = () => {
      const vFovTan = 2 * Math.tan((camera.fov * Math.PI) / 360);
      controls.panSpeed =
        vFovTan * Math.sqrt(Math.max(camera.aspect, 0.5)) * PAN_FEEL;
    };
    updatePanSpeed();

    controls.mouseButtons = {
      LEFT: null,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    };
    controls.noRotate = isSmall;
    controls.noZoom = isSmall;
    controls.noPan = isSmall;
    controls.staticMoving = false;
    controls.dynamicDampingFactor = DAMPING;
    controls.minDistance = 0.02;
    controls.maxDistance = 100000;
    controls.target.set(0, 0, 0);
    controlsRef.current = controls;

    const markDirty = () => { needsRender = true; };
    controls.addEventListener("change", markDirty);

    const onInteractStart = () => {
      if (restoreTimer) { clearTimeout(restoreTimer); restoreTimer = null; }
      if (isHeavy && renderer.getPixelRatio() !== 1) {
        renderer.setPixelRatio(1);
        needsRender = true;
      }
    };
    const onInteractEnd = () => {
      if (!isHeavy) return;
      if (restoreTimer) clearTimeout(restoreTimer);
      restoreTimer = setTimeout(() => {
        if (disposed) return;
        renderer.setPixelRatio(fullPixelRatio);
        needsRender = true;
      }, 500);
    };
    controls.addEventListener("start", onInteractStart);
    controls.addEventListener("end", onInteractEnd);

    camera.add(new THREE.AmbientLight(0xffffff, 0.55));
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
    keyLight.position.set(1, 1.5, 2);
    camera.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
    fillLight.position.set(-2, -0.5, 1);
    camera.add(fillLight);
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.35);
    rimLight.position.set(0, 1, -2);
    camera.add(rimLight);

    const updateClipping = () => {
      const distance = camera.position.distanceTo(controls.target);
      const near = Math.max(radius * 0.001, (distance - radius * 1.5) * 0.5);
      const far = Math.max(distance + radius * 4, near + 1);
      if (
        Math.abs(camera.near - near) > 1e-6 ||
        Math.abs(camera.far - far) > 1e-3
      ) {
        camera.near = near;
        camera.far = far;
        camera.updateProjectionMatrix();
      }
    };
    controls.addEventListener("change", updateClipping);

    const computeTangentDistance = () => {
      const vFov = (camera.fov * Math.PI) / 180;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
      const limiting = Math.min(vFov, hFov);
      return radius / Math.sin(limiting / 2);
    };

    const setView = (name = "default") => {
      const direction = (VIEW_DIRECTIONS[name] || VIEW_DIRECTIONS.default)
        .clone().normalize();

      if (
        !isMulti &&
        (name === "default" || !VIEW_DIRECTIONS[name]) &&
        meshesRef.current[0]
      ) {
        const m = meshesRef.current[0];
        if (Math.abs(m.quaternion.w) < 0.999999) {
          const inv = m.quaternion.clone().invert();
          m.quaternion.identity();
          m.updateMatrixWorld(true);
          rotatePlaneWithModel(m, inv, m.position.clone());
        }
      }

      let distance;
      if (name === "default" || !VIEW_DIRECTIONS[name]) {
        distance = fitDistance;
        controls.target.set(0, 0, 0);
      } else {
        distance = camera.position.distanceTo(controls.target);
        if (!Number.isFinite(distance) || distance <= 0) {
          distance = fitDistance;
          controls.target.set(0, 0, 0);
        }
        distance = THREE.MathUtils.clamp(
          distance, controls.minDistance, maxDistanceLimit
        );
      }

      const isTopOrBottom = name === "top" || name === "bottom";
      camera.up.set(0, isTopOrBottom ? 0 : 1, isTopOrBottom ? 1 : 0);

      camera.position.copy(controls.target).addScaledVector(direction, distance);
      camera.lookAt(controls.target);
      controls.update();
      controls.handleResize?.();
      updateClipping();
      needsRender = true;
    };

    let persistTimer = null;
    const persistState = () => {
      if (isSmall || meshesRef.current.length === 0) return;
      try {
        const firstMesh = meshesRef.current[0];
        saveViewerState(isMulti ? fileUrls[0]?.url : fileUrl, {
          wireframe: wireframeRef.current,
          autoRotate: autoRotateRef.current,
          opacity: Math.round(opacityRef.current * 100),
          showOpacity, expanded, sidebarHidden,
          showCrossSection: sectionActiveRef.current,
          clipEnabled: clipEnabledRef.current,
          clipInverted: clipInvertedRef.current,
          planeOffset: planeOffsetRef.current,
          planeSizeMult: planeSizeMultRef.current,
          planeNormal: { x: planeNormal.x, y: planeNormal.y, z: planeNormal.z },
          planeUp: { x: planeUp.x, y: planeUp.y, z: planeUp.z },
          planeShift: { x: planeShift.x, y: planeShift.y, z: planeShift.z },
          camera: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          target: { x: controls.target.x, y: controls.target.y, z: controls.target.z },
          up: { x: camera.up.x, y: camera.up.y, z: camera.up.z },
          measurePts: measurePts.map((v) => ({ x: v.x, y: v.y, z: v.z })),
          meshQuat: !isMulti
            ? {
                x: firstMesh.quaternion.x,
                y: firstMesh.quaternion.y,
                z: firstMesh.quaternion.z,
                w: firstMesh.quaternion.w,
              }
            : undefined,
        });
      } catch {
        /* ignore */
      }
    };
    const schedulePersist = () => {
      if (isSmall || disposed) return;
      if (persistTimer) clearTimeout(persistTimer);
      persistTimer = setTimeout(() => {
        persistTimer = null;
        persistState();
      }, 400);
    };

    viewApiRef.current = {
      setView,
      requestRender: () => { needsRender = true; },
      persistState,
      schedulePersist,
    };

    controls.addEventListener("end", schedulePersist);

    const constrainView = () => {
      if (meshesRef.current.length === 0) return;
      const p = camera.position;
      const t = controls.target;
      if (!Number.isFinite(p.x + p.y + p.z) || !Number.isFinite(t.x + t.y + t.z)) {
        setView("default");
        return;
      }
      const dist = p.distanceTo(t);
      const halfH = dist * Math.tan((camera.fov * Math.PI) / 360);
      const halfW = halfH * camera.aspect;
      const limX = halfW * PAN_EDGE_FACTOR;
      const limY = halfH * PAN_EDGE_FACTOR;

      camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
      camUp.set(0, 1, 0).applyQuaternion(camera.quaternion);

      const offX = t.dot(camRight);
      const offY = t.dot(camUp);
      const clampedX = THREE.MathUtils.clamp(offX, -limX, limX);
      const clampedY = THREE.MathUtils.clamp(offY, -limY, limY);

      if (clampedX !== offX || clampedY !== offY) {
        tmpVec.copy(camRight).multiplyScalar(clampedX - offX)
          .addScaledVector(camUp, clampedY - offY);
        t.add(tmpVec);
        p.add(tmpVec);
        needsRender = true;
      }

      const curDist = p.distanceTo(t);
      if (curDist > maxDistanceLimit) {
        tmpVec.copy(p).sub(t).setLength(maxDistanceLimit);
        p.copy(t).add(tmpVec);
        needsRender = true;
      }
    };

    const handleResize = () => {
      if (disposed || isSmall) return;
      const { w, h } = getSize();
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      controls.handleResize?.();
      updatePanSpeed();
      updateClipping();
      needsRender = true;
    };

    if (!isSmall && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(mount);
    } else if (!isSmall) {
      window.addEventListener("resize", handleResize);
    }

    const clipPlane = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0);
    const planeNormal = new THREE.Vector3(1, 0, 0);
    const planeUp = new THREE.Vector3(0, 1, 0);
    const planeShift = new THREE.Vector3(0, 0, 0);

    let clipPlaneGroup = null;
    let clipDiscMesh = null;
    let clipBorderLine = null;
    let clipSizeHandle = null;
    let gizmoPickables = [];
    let gizmoSubs = [];
    let lastGizmoScale = -1;
    let crossLines = null;
    let gizmoRedMat = null;
    let gizmoBlueMat = null;
    let gizmoGreenMat = null;
    let gizmoPurpleMat = null;
    let gizmoActive = false;

    const raycaster = new THREE.Raycaster();
    const pointerNDC = new THREE.Vector2();
    let drag = null;

    const updatePointerNDC = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointerNDC.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      pointerNDC.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    };

    const toScreenPx = (v3) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const p = v3.clone().project(camera);
      return new THREE.Vector2(
        (p.x * 0.5 + 0.5) * rect.width,
        (-p.y * 0.5 + 0.5) * rect.height
      );
    };

    const disposeGroup = (g) => {
      g.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
          else o.material.dispose();
        }
      });
    };

    const makeGizmoMat = (color) =>
      new THREE.MeshStandardMaterial({
        color,
        roughness: 0.45,
        metalness: 0.1,
        depthTest: false,
        depthWrite: false,
        transparent: true,
      });

    const makeHaloMat = () =>
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.92,
      });

    const makeHitMat = () =>
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthTest: false,
        depthWrite: false,
      });

    const Y_AXIS = new THREE.Vector3(0, 1, 0);

    const makeStraightDoubleArrow = (dir, halfLen, tubeR, coneR, coneLen, mat, grow = 0) => {
      const g = new THREE.Group();
      const shaft = new THREE.Mesh(
        new THREE.CylinderGeometry(tubeR + grow, tubeR + grow, halfLen * 2, 14),
        mat
      );
      shaft.quaternion.setFromUnitVectors(Y_AXIS, dir);
      g.add(shaft);
      [1, -1].forEach((sgn) => {
        const d = dir.clone().multiplyScalar(sgn);
        const cl = coneLen + grow * 2;
        const cone = new THREE.Mesh(new THREE.ConeGeometry(coneR + grow * 1.4, cl, 24), mat);
        cone.quaternion.setFromUnitVectors(Y_AXIS, d);
        cone.position.copy(d).multiplyScalar(halfLen - grow + cl / 2);
        g.add(cone);
      });
      return g;
    };

    const makeCurvedDoubleArrow = (radial, tangent, R, phi, tubeR, coneR, coneLen, mat, grow = 0) => {
      const g = new THREE.Group();

      const r = radial.clone().normalize();
      const t = tangent.clone();
      t.sub(r.clone().multiplyScalar(t.dot(r)));
      if (t.lengthSq() < 1e-8) t.set(0, 0, 1);
      t.normalize();

      const pts = [];
      const steps = 28;
      for (let i = 0; i <= steps; i++) {
        const th = -phi + (2 * phi * i) / steps;
        const px = R * (1 - Math.cos(th));
        const py = R * Math.sin(th);
        pts.push(
          new THREE.Vector3()
            .addScaledVector(r, -px)
            .addScaledVector(t, py)
        );
      }
      const curve = new THREE.CatmullRomCurve3(pts);
      g.add(
        new THREE.Mesh(new THREE.TubeGeometry(curve, 40, tubeR + grow, 12, false), mat)
      );

      const endDir = new THREE.Vector3()
        .addScaledVector(r, -Math.sin(phi))
        .addScaledVector(t, Math.cos(phi))
        .normalize();
      const startDir = new THREE.Vector3()
        .addScaledVector(r, Math.sin(phi))
        .addScaledVector(t, Math.cos(phi))
        .normalize();

      const cl = coneLen + grow * 2;

      {
        const pt = pts[pts.length - 1];
        const cone = new THREE.Mesh(new THREE.ConeGeometry(coneR + grow * 1.4, cl, 24), mat);
        cone.quaternion.setFromUnitVectors(Y_AXIS, endDir);
        cone.position.copy(pt).addScaledVector(endDir, -grow + cl / 2);
        g.add(cone);
      }
      {
        const pt = pts[0];
        const back = startDir.clone().negate();
        const cone = new THREE.Mesh(new THREE.ConeGeometry(coneR + grow * 1.4, cl, 24), mat);
        cone.quaternion.setFromUnitVectors(Y_AXIS, back);
        cone.position.copy(pt).addScaledVector(back, -grow + cl / 2);
        g.add(cone);
      }
      return g;
    };

    const registerSub = (sub, id) => {
      sub.traverse((o) => {
        if (!o.isMesh) return;
        if (o.userData.halo) {
          o.renderOrder = 9;
          return;
        }
        o.userData.handleId = id;
        o.renderOrder = 10;
        gizmoPickables.push(o);
      });
      gizmoSubs.push(sub);
    };

    const markHalo = (obj) => {
      obj.traverse((o) => { if (o.isMesh) o.userData.halo = true; });
      return obj;
    };

    const buildClipPlaneVisual = () => {
      if (clipPlaneGroup) {
        scene.remove(clipPlaneGroup);
        disposeGroup(clipPlaneGroup);
      }
      gizmoPickables = [];
      gizmoSubs = [];
      lastGizmoScale = -1;

      const size = radius * planeSizeMultRef.current;
      clipPlaneGroup = new THREE.Group();

      const discGeo = new THREE.CircleGeometry(size, 96);
      const discMat = new THREE.MeshBasicMaterial({
        color: CLIP_PLANE_COLOR,
        transparent: true,
        opacity: CLIP_PLANE_OPACITY,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      clipDiscMesh = new THREE.Mesh(discGeo, discMat);
      clipDiscMesh.renderOrder = 6;
      clipDiscMesh.userData.handleId = "disc";
      gizmoPickables.push(clipDiscMesh);
      clipPlaneGroup.add(clipDiscMesh);

      const ringPts = [];
      const segs = 128;
      for (let i = 0; i <= segs; i++) {
        const a = (i / segs) * Math.PI * 2;
        ringPts.push(new THREE.Vector3(Math.cos(a) * size, Math.sin(a) * size, 0));
      }
      const ringGeo = new THREE.BufferGeometry().setFromPoints(ringPts);
      const ringMat = new THREE.LineBasicMaterial({
        color: CLIP_PLANE_EDGE,
        transparent: true,
        opacity: 0.95,
      });
      clipBorderLine = new THREE.Line(ringGeo, ringMat);
      clipBorderLine.renderOrder = 7;
      clipPlaneGroup.add(clipBorderLine);

      const redMat = makeGizmoMat(CLIP_HANDLE_RED);
      const blueMat = makeGizmoMat(CLIP_HANDLE_BLUE);
      const greenMat = makeGizmoMat(CLIP_HANDLE_GREEN);
      const purpleMat = makeGizmoMat(CLIP_HANDLE_PURPLE);
      gizmoRedMat = redMat;
      gizmoBlueMat = blueMat;
      gizmoGreenMat = greenMat;
      gizmoPurpleMat = purpleMat;
      gizmoActive = false;
      const grayMat = makeGizmoMat(CLIP_ARROW_COLOR);
      const haloMat = makeHaloMat();
      const hitMat = makeHitMat();

      const handleR = radius * 0.028;
      const tubeR = radius * 0.006;
      const coneR = radius * 0.018;
      const coneLen = radius * 0.04;
      const halo = radius * 0.004;
      const arcPhi = 0.16;
      const normalHalfLen = radius * 0.18;

      const addSphereWithHalo = (sub, mat) => {
        sub.add(
          markHalo(new THREE.Mesh(new THREE.SphereGeometry(handleR + halo, 28, 28), haloMat))
        );
        sub.add(new THREE.Mesh(new THREE.SphereGeometry(handleR, 28, 28), mat));
        sub.add(new THREE.Mesh(new THREE.SphereGeometry(handleR * 2.2, 12, 12), hitMat));
      };

      {
        const sub = new THREE.Group();
        const dirZ = new THREE.Vector3(0, 0, 1);
        sub.add(
          markHalo(
            makeStraightDoubleArrow(dirZ, normalHalfLen, tubeR, coneR, coneLen, haloMat, halo)
          )
        );
        sub.add(makeStraightDoubleArrow(dirZ, normalHalfLen, tubeR, coneR, coneLen, grayMat));
        addSphereWithHalo(sub, blueMat);
        registerSub(sub, "translate");
        clipPlaneGroup.add(sub);
      }

      const handleColors = {
        rotX: greenMat,
        rotY: redMat,
        rotZ: purpleMat,
      };
      ROT_HANDLES.forEach((h) => {
        const radial = new THREE.Vector3(...h.radial).normalize();
        const localNormal = new THREE.Vector3(0, 0, 1);

        let tangent = new THREE.Vector3().crossVectors(localNormal, radial);
        if (tangent.lengthSq() < 1e-8) tangent.set(1, 0, 0);
        tangent.normalize();

        const handlePos = radial.clone().multiplyScalar(size);

        const sub = new THREE.Group();
        sub.position.copy(handlePos);

        sub.add(
          markHalo(
            makeCurvedDoubleArrow(radial, tangent, size, arcPhi, tubeR, coneR, coneLen, haloMat, halo)
          )
        );
        sub.add(
          makeCurvedDoubleArrow(radial, tangent, size, arcPhi, tubeR, coneR, coneLen, grayMat)
        );

        addSphereWithHalo(sub, handleColors[h.id] || redMat);
        registerSub(sub, h.id);
        clipPlaneGroup.add(sub);
      });

      {
        const sub = new THREE.Group();
        sub.position.set(size, 0, 0);

        const sizeHandleR = radius * 0.032;

        sub.add(
          markHalo(
            new THREE.Mesh(
              new THREE.SphereGeometry(sizeHandleR + halo, 20, 20),
              haloMat
            )
          )
        );

        const diamondMat = makeGizmoMat(CLIP_PLANE_EDGE_INVERTED);
        const diamond = new THREE.Mesh(
          new THREE.OctahedronGeometry(sizeHandleR, 0),
          diamondMat
        );
        sub.add(diamond);

        sub.add(
          new THREE.Mesh(
            new THREE.SphereGeometry(sizeHandleR * 2.5, 12, 12),
            hitMat
          )
        );

        clipSizeHandle = sub;
        registerSub(sub, "size");
        clipPlaneGroup.add(sub);
      }

      scene.add(clipPlaneGroup);
      applyPlaneTransform();
    };

    const applyPlaneTransform = () => {
      const n = planeNormal.clone().normalize();
      const u = planeUp.clone();
      u.sub(n.clone().multiplyScalar(u.dot(n)));
      if (u.lengthSq() < 1e-8) {
        u.set(0, 1, 0);
        if (Math.abs(n.y) > 0.95) u.set(0, 0, 1);
        u.sub(n.clone().multiplyScalar(u.dot(n)));
      }
      u.normalize();
      planeNormal.copy(n);
      planeUp.copy(u);

      planeShift.sub(n.clone().multiplyScalar(planeShift.dot(n)));

      const s = planeOffsetRef.current * radius * CLIP_OFFSET_RANGE;
      const center = n.clone().multiplyScalar(s).add(planeShift);
      const nEff = clipInvertedRef.current ? n.clone().negate() : n.clone();
      clipPlane.setFromNormalAndCoplanarPoint(nEff, center);

      if (clipPlaneGroup) {
        const right = new THREE.Vector3().crossVectors(u, n).normalize();
        const m = new THREE.Matrix4().makeBasis(right, u, n);
        clipPlaneGroup.quaternion.setFromRotationMatrix(m);
        clipPlaneGroup.position.copy(center);
        if (clipBorderLine) {
          clipBorderLine.material.color.setHex(
            gizmoActive || clipInvertedRef.current
              ? CLIP_PLANE_EDGE_INVERTED
              : CLIP_PLANE_EDGE
          );
        }
      }
      needsRender = true;
    };

    const setGizmoHighlight = (on, id) => {
      gizmoActive = on;
      const yellow = CLIP_PLANE_EDGE_INVERTED;
      if (gizmoBlueMat) gizmoBlueMat.color.setHex(on && id === "translate" ? yellow : CLIP_HANDLE_BLUE);
      if (gizmoGreenMat) gizmoGreenMat.color.setHex(on && id === "rotX" ? yellow : CLIP_HANDLE_GREEN);
      if (gizmoRedMat) gizmoRedMat.color.setHex(on && id === "rotY" ? yellow : CLIP_HANDLE_RED);
      if (gizmoPurpleMat) gizmoPurpleMat.color.setHex(on && id === "rotZ" ? yellow : CLIP_HANDLE_PURPLE);
      applyPlaneTransform();
    };

    const clearCrossLines = () => {
      if (crossLines) crossLines.visible = false;
    };

    const updateCrossLines = (flat) => {
      if (!flat || flat.length === 0) { clearCrossLines(); return; }
      const arr = new Float32Array(flat);
      if (!crossLines) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(arr, 3));
        crossLines = new THREE.LineSegments(
          geo,
          new THREE.LineBasicMaterial({
            color: CLIP_OUTLINE_COLOR,
            depthTest: false,
            transparent: true,
          })
        );
        crossLines.renderOrder = 4;
        crossLines.frustumCulled = false;
        scene.add(crossLines);
      } else {
        crossLines.geometry.dispose();
        crossLines.geometry = new THREE.BufferGeometry();
        crossLines.geometry.setAttribute("position", new THREE.BufferAttribute(arr, 3));
      }
      crossLines.visible = true;
      needsRender = true;
    };

    const computeCrossSection = () => {
      const meshes = visMeshes();
      if (meshes.length === 0 || !sectionActiveRef.current) {
        clearCrossLines();
        setCrossSectionOutline(null);
        needsRender = true;
        return;
      }

      const n = planeNormal;
      const sW = planeOffsetRef.current * radius * CLIP_OFFSET_RANGE;

      const allSegs2D = [];
      const allP = [];
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

      const tmp = [0, 0, 0];

      for (const mesh of meshes) {
        const geometry = mesh.geometry;
        const pos = geometry.attributes.position;
        const index = geometry.index;
        const count = pos.count;
        const triCount = index ? index.count / 3 : count / 3;

        mesh.updateMatrixWorld(true);
        const e = mesh.matrixWorld.elements;

        const W = new Float64Array(count * 3);
        const D = new Float64Array(count);
        for (let i = 0; i < count; i++) {
          const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
          const wx = e[0] * x + e[4] * y + e[8] * z + e[12];
          const wy = e[1] * x + e[5] * y + e[9] * z + e[13];
          const wz = e[2] * x + e[6] * y + e[10] * z + e[14];
          W[i * 3] = wx; W[i * 3 + 1] = wy; W[i * 3 + 2] = wz;
          D[i] = n.x * wx + n.y * wy + n.z * wz - sW;
        }

        const edgePoint = (i, j, out) => {
          let a = i, b = j;
          const ax = W[i * 3], ay = W[i * 3 + 1], az = W[i * 3 + 2];
          const bx = W[j * 3], by = W[j * 3 + 1], bz = W[j * 3 + 2];
          if (ax > bx || (ax === bx && (ay > by || (ay === by && az > bz)))) {
            a = j; b = i;
          }
          const da = D[a], db = D[b];
          const t = da / (da - db);
          const px = W[a * 3], py = W[a * 3 + 1], pz = W[a * 3 + 2];
          out[0] = px + (W[b * 3] - px) * t;
          out[1] = py + (W[b * 3 + 1] - py) * t;
          out[2] = pz + (W[b * 3 + 2] - pz) * t;
        };

        const idx = index ? index.array : null;
        const P = [];
        for (let t = 0; t < triCount && P.length < CLIP_MAX_SEGMENTS * 6; t++) {
          const i0 = idx ? idx[t * 3] : t * 3;
          const i1 = idx ? idx[t * 3 + 1] : t * 3 + 1;
          const i2 = idx ? idx[t * 3 + 2] : t * 3 + 2;
          const s0 = D[i0] >= 0, s1 = D[i1] >= 0, s2 = D[i2] >= 0;
          if (s0 === s1 && s1 === s2) continue;
          if (s0 !== s1) { edgePoint(i0, i1, tmp); P.push(tmp[0], tmp[1], tmp[2]); }
          if (s1 !== s2) { edgePoint(i1, i2, tmp); P.push(tmp[0], tmp[1], tmp[2]); }
          if (s2 !== s0) { edgePoint(i2, i0, tmp); P.push(tmp[0], tmp[1], tmp[2]); }
        }

        for (let q = 0; q < P.length; q++) allP.push(P[q]);

        const up = planeUp;
        const right = new THREE.Vector3().crossVectors(planeUp, planeNormal).normalize();

        const segCount = Math.floor(P.length / 6);
        for (let k = 0; k < segCount; k++) {
          const o = k * 6;
          const x1 = P[o] * right.x + P[o + 1] * right.y + P[o + 2] * right.z;
          const y1 = P[o] * up.x + P[o + 1] * up.y + P[o + 2] * up.z;
          const x2 = P[o + 3] * right.x + P[o + 4] * right.y + P[o + 5] * right.z;
          const y2 = P[o + 3] * up.x + P[o + 4] * up.y + P[o + 5] * up.z;
          if (x1 < minX) minX = x1; if (x2 < minX) minX = x2;
          if (x1 > maxX) maxX = x1; if (x2 > maxX) maxX = x2;
          if (y1 < minY) minY = y1; if (y2 < minY) minY = y2;
          if (y1 > maxY) maxY = y1; if (y2 > maxY) maxY = y2;
          allSegs2D.push({ x1, y1, x2, y2 });
        }
      }

      if (allP.length > 0) {
        updateCrossLines(allP);
      }

      if (allSegs2D.length === 0 || !Number.isFinite(minX)) {
        clearCrossLines();
        setCrossSectionOutline(null);
        needsRender = true;
        return;
      }

      const width = maxX - minX;
      const height = maxY - minY;

      const padX = width * 0.1 || 1;
      const padY = height * 0.1 || 1;
      minX -= padX; maxX += padX;
      minY -= padY; maxY += padY;

      setCrossSectionOutline({
        segments: allSegs2D,
        bounds: { minX, maxX, minY, maxY },
        axisX: { from: { x: minX, y: 0 }, to: { x: maxX, y: 0 } },
        axisY: { from: { x: 0, y: minY }, to: { x: 0, y: maxY } },
      });

      needsRender = true;
    };

    let crossSectionTimer = null;
    const scheduleCrossSectionUpdate = (delay = 60) => {
      if (crossSectionTimer) clearTimeout(crossSectionTimer);
      crossSectionTimer = setTimeout(() => {
        if (disposed) return;
        computeCrossSection();
      }, delay);
    };

    clipApiRef.current = {
      setEnabled: (on) => {
        clipEnabledRef.current = on;
        meshesRef.current.forEach((mesh) => {
          if (mesh.material) {
            mesh.material.clippingPlanes = on ? [clipPlane] : [];
            mesh.material.needsUpdate = true;
          }
        });
        needsRender = true;
      },
      setSectionActive: (on) => {
        sectionActiveRef.current = on;

        if (on) {
          if (!clipPlaneGroup && meshesRef.current.length > 0) buildClipPlaneVisual();
          if (clipPlaneGroup) clipPlaneGroup.visible = true;
          applyPlaneTransform();
          scheduleCrossSectionUpdate(0);
        } else {
          if (clipPlaneGroup) clipPlaneGroup.visible = false;
          clearCrossLines();
          setCrossSectionOutline(null);
          sectionOwner = null;
        }
        needsRender = true;
      },
      setInverted: (inv) => {
        clipInvertedRef.current = inv;
        applyPlaneTransform();
        scheduleCrossSectionUpdate();
      },
      setOffset: (off) => {
        planeOffsetRef.current = off;
        applyPlaneTransform();
        scheduleCrossSectionUpdate();
      },
      setSizeMult: (mult) => {
        const clamped = THREE.MathUtils.clamp(
          mult,
          CLIP_PLANE_SIZE_MULT_MIN,
          CLIP_PLANE_SIZE_MULT_MAX
        );
        planeSizeMultRef.current = clamped;
        if (clipPlaneGroup && meshesRef.current.length > 0) {
          buildClipPlaneVisual();
          if (clipPlaneGroup) clipPlaneGroup.visible = sectionActiveRef.current;
          applyPlaneTransform();
        }
        needsRender = true;
      },
      recompute: (delay = 0) => scheduleCrossSectionUpdate(delay),
      resetOrientation: () => {
        planeNormal.set(1, 0, 0);
        planeUp.set(0, 1, 0);
        planeShift.set(0, 0, 0);
        applyPlaneTransform();
        scheduleCrossSectionUpdate();
        schedulePersist();
      },
      setPlaneFromNormal: (nWorld, offsetWorld, sizeHint, shiftWorld) => {
        const n = nWorld.clone().normalize();

        const upGuess = Math.abs(n.y) > 0.95
          ? new THREE.Vector3(0, 0, 1)
          : new THREE.Vector3(0, 1, 0);

        planeNormal.copy(n);
        planeUp.copy(upGuess);
        planeUp.sub(n.clone().multiplyScalar(planeUp.dot(n)));
        if (planeUp.lengthSq() < 1e-8) planeUp.set(0, 0, 1);
        planeUp.normalize();

        const off = THREE.MathUtils.clamp(
          offsetWorld / (radius * CLIP_OFFSET_RANGE),
          -1, 1
        );
        planeOffsetRef.current = off;

        if (shiftWorld) {
          planeShift.copy(shiftWorld);
        } else {
          planeShift.set(0, 0, 0);
        }

        if (typeof sizeHint === "number" && sizeHint > 0 && radius > 0) {
          const mult = sizeHint / radius;
          planeSizeMultRef.current = THREE.MathUtils.clamp(
            mult,
            CLIP_PLANE_SIZE_MULT_MIN,
            CLIP_PLANE_SIZE_MULT_MAX
          );
        }

        applyPlaneTransform();
        scheduleCrossSectionUpdate(0);
        schedulePersist();
        return { offset: off, sizeMult: planeSizeMultRef.current };
      },
    };

    let drawGroup = null;
    let drawLineHalo = null;
    let drawLine = null;
    let drawDots = [];
    let drawDotHalos = [];
    let drawState = null;
    let drawStartPoint = null;

    let sectionOwner = null;

    const pickSectionOwner = (A, B) => {
      const meshes = visMeshes();
      if (meshes.length === 0) return null;
      if (meshes.length === 1) return meshes[0];

      camera.updateMatrixWorld();
      const rect = renderer.domElement.getBoundingClientRect();
      const tanHalf = Math.tan((camera.fov * Math.PI) / 360);
      const a = toScreenPx(A);
      const b = toScreenPx(B);
      const ab = b.clone().sub(a);
      const abLenSq = Math.max(ab.lengthSq(), 1e-9);

      let best = null;
      let bestScore = Infinity;
      meshes.forEach((m) => {
        const bs = m.geometry.boundingSphere;
        if (!bs) return;
        const c = m.position;
        const dist = camera.position.distanceTo(c);
        if (dist <= 0) return;
        const sp = toScreenPx(c);
        const pxR = Math.max((bs.radius / (dist * tanHalf)) * (rect.height / 2), 1);
        const t = THREE.MathUtils.clamp(sp.clone().sub(a).dot(ab) / abLenSq, 0, 1);
        const closest = a.clone().add(ab.clone().multiplyScalar(t));
        const score = sp.distanceTo(closest) / pxR;
        if (score < bestScore) {
          bestScore = score;
          best = m;
        }
      });
      return best;
    };

    const buildDrawVisual = () => {
      if (drawGroup) return;
      drawGroup = new THREE.Group();
      drawGroup.visible = false;

      const lineMat = new THREE.LineBasicMaterial({
        color: DRAW_LINE_COLOR,
        depthTest: false,
        transparent: true,
      });
      const lineHaloMat = new THREE.LineBasicMaterial({
        color: DRAW_LINE_HALO_COLOR,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });

      const geo = new THREE.BufferGeometry();
      geo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(6), 3)
      );

      drawLineHalo = new THREE.Line(geo.clone(), lineHaloMat);
      drawLineHalo.renderOrder = 29;
      drawLineHalo.frustumCulled = false;
      drawGroup.add(drawLineHalo);

      drawLine = new THREE.Line(geo, lineMat);
      drawLine.renderOrder = 30;
      drawLine.frustumCulled = false;
      drawGroup.add(drawLine);

      const dotMat = new THREE.MeshBasicMaterial({
        color: DRAW_LINE_COLOR,
        depthTest: false,
        depthWrite: false,
        transparent: true,
      });
      const dotHaloMat = new THREE.MeshBasicMaterial({
        color: DRAW_LINE_HALO_COLOR,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.95,
      });

      for (let i = 0; i < 2; i++) {
        const halo = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), dotHaloMat);
        halo.renderOrder = 29;
        const dot = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), dotMat);
        dot.renderOrder = 30;
        drawGroup.add(halo, dot);
        drawDots.push(dot);
        drawDotHalos.push(halo);
      }

      scene.add(drawGroup);
    };

    const clearDrawVisual = () => {
      drawState = null;
      drawStartPoint = null;
      if (drawGroup) {
        drawGroup.visible = false;
      }
      needsRender = true;
    };

    const updateDrawVisual = () => {
      if (!drawGroup || !drawState) return;

      const A = drawState.startWorld;
      const B = drawState.endWorld;

      const posAttr = drawLine.geometry.attributes.position;
      posAttr.setXYZ(0, A.x, A.y, A.z);
      posAttr.setXYZ(1, B.x, B.y, B.z);
      posAttr.needsUpdate = true;

      const posAttrHalo = drawLineHalo.geometry.attributes.position;
      posAttrHalo.setXYZ(0, A.x, A.y, A.z);
      posAttrHalo.setXYZ(1, B.x, B.y, B.z);
      posAttrHalo.needsUpdate = true;

      const midWorld = A.clone().add(B).multiplyScalar(0.5);
      const distCam = camera.position.distanceTo(midWorld);
      const hPx = Math.max(renderer.domElement.clientHeight, 1);
      const worldPerPx = (2 * distCam * Math.tan((camera.fov * Math.PI) / 360)) / hPx;

      drawDots[0].position.copy(A);
      drawDots[1].position.copy(B);
      drawDots[0].scale.setScalar(DRAW_DOT_PX * worldPerPx);
      drawDots[1].scale.setScalar(DRAW_DOT_PX * worldPerPx);

      drawDotHalos[0].position.copy(A);
      drawDotHalos[1].position.copy(B);
      drawDotHalos[0].scale.setScalar((DRAW_DOT_PX + DRAW_DOT_HALO_PX) * worldPerPx);
      drawDotHalos[1].scale.setScalar((DRAW_DOT_PX + DRAW_DOT_HALO_PX) * worldPerPx);

      drawGroup.visible = true;
      needsRender = true;
    };

    const pickDrawPoint = (clientX, clientY) => {
      updatePointerNDC(clientX, clientY);
      raycaster.setFromCamera(pointerNDC, camera);

      const camDir = new THREE.Vector3();
      camera.getWorldDirection(camDir);
      const planeThroughOrigin = new THREE.Plane().setFromNormalAndCoplanarPoint(
        camDir,
        new THREE.Vector3(0, 0, 0)
      );
      const pt = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(planeThroughOrigin, pt)) {
        return pt.clone();
      }
      return null;
    };

    let sectionDragActive = false;

    const onSectionDrawDown = (ev) => {
      if (!sectionDrawModeRef.current) return;
      if (ev.button !== 0) return;
      if (meshesRef.current.length === 0) return;
      const p = pickDrawPoint(ev.clientX, ev.clientY);
      if (!p) return;

      buildDrawVisual();
      drawState = {
        startWorld: p.clone(),
        endWorld: p.clone(),
      };
      updateDrawVisual();
      sectionDragActive = true;
      controls.enabled = false;
      renderer.domElement.style.cursor = "crosshair";
      ev.preventDefault();
      ev.stopPropagation();
    };

    const onSectionDrawMove = (ev) => {
      if (!sectionDragActive || !drawState) return;
      const p = pickDrawPoint(ev.clientX, ev.clientY);
      if (p) {
        drawState.endWorld = p;
        updateDrawVisual();
      }
    };

    const onSectionDrawUp = (ev) => {
      if (!sectionDragActive || !drawState) return;
      sectionDragActive = false;
      controls.enabled = true;
      renderer.domElement.style.cursor = "crosshair";

      const A = drawState.startWorld;
      const B = drawState.endWorld;
      const dir = new THREE.Vector3().subVectors(B, A);
      const len = dir.length();

      if (len < radius * 0.01) {
        clearDrawVisual();
        return;
      }

      camera.updateMatrixWorld();
      const viewDir = new THREE.Vector3();
      camera.getWorldDirection(viewDir);
      const lineDir = dir.clone().normalize();

      const nWorld = new THREE.Vector3()
        .crossVectors(viewDir, lineDir)
        .normalize();

      if (nWorld.lengthSq() < 1e-6) {
        nWorld.crossVectors(lineDir, camera.up).normalize();
      }

      const mid = A.clone().add(B).multiplyScalar(0.5);
      const offsetWorld = nWorld.dot(mid);

      const clampedOffsetWorld =
        THREE.MathUtils.clamp(
          offsetWorld / (radius * CLIP_OFFSET_RANGE),
          -1, 1
        ) * radius * CLIP_OFFSET_RANGE;
      const shiftWorld = mid
        .clone()
        .sub(nWorld.clone().multiplyScalar(clampedOffsetWorld));

      const sizeHint = len * 0.5;

      sectionOwner = pickSectionOwner(A, B);

      if (clipApiRef.current) {
        const result = clipApiRef.current.setPlaneFromNormal(
          nWorld,
          offsetWorld,
          sizeHint,
          shiftWorld
        );
        if (result) {
          setPlaneSizeMult(result.sizeMult);
        }
      }

      setPlaneOffset(() => {
        const off = THREE.MathUtils.clamp(
          offsetWorld / (radius * CLIP_OFFSET_RANGE), -1, 1
        );
        return off;
      });

      setShowCrossSection(true);

      setSectionDrawMode(false);
      sectionDrawModeRef.current = false;
      clearDrawVisual();
    };

    const onSectionDrawDownCapture = (ev) => onSectionDrawDown(ev);
    const onSectionDrawMoveCapture = (ev) => onSectionDrawMove(ev);
    const onSectionDrawUpCapture = (ev) => onSectionDrawUp(ev);

    renderer.domElement.addEventListener("pointerdown", onSectionDrawDownCapture, true);
    window.addEventListener("pointermove", onSectionDrawMoveCapture, true);
    window.addEventListener("pointerup", onSectionDrawUpCapture, true);
    window.addEventListener("pointercancel", onSectionDrawUpCapture, true);

    const measurePts = [];
    let measureGroup = null;
    let measureTube = null;
    let measureMat = null;
    let measureHitMat = null;
    let measureHaloMat = null;
    let measureTubeHalo = null;
    const measureCones = [];
    const measureDots = [];
    const measureHits = [];
    const measureConeHalos = [];
    const measureDotHalos = [];
    let measureDrag = null;
    const mTmpW = new THREE.Vector3();
    const mDir = new THREE.Vector3();
    const mMid = new THREE.Vector3();

    const buildMeasureVisual = () => {
      measureGroup = new THREE.Group();
      measureGroup.visible = false;

      measureMat = new THREE.MeshBasicMaterial({
        color: MEASURE_COLOR,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.95,
      });

      const measureAccentMat = new THREE.MeshBasicMaterial({
        color: MEASURE_ACCENT,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.95,
      });

      measureHitMat = makeHitMat();
      measureHaloMat = new THREE.MeshBasicMaterial({
        color: MEASURE_HALO_COLOR,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.85,
      });

      measureTubeHalo = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1, 12), measureHaloMat);
      measureTubeHalo.renderOrder = 19;
      measureGroup.add(measureTubeHalo);

      measureTube = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1, 12), measureMat);
      measureTube.renderOrder = 20;
      measureGroup.add(measureTube);

      for (let i = 0; i < 2; i++) {
        const coneHalo = new THREE.Mesh(new THREE.ConeGeometry(1, 1, 20), measureHaloMat);
        coneHalo.renderOrder = 19;

        const dotHalo = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), measureHaloMat);
        dotHalo.renderOrder = 19;

        const cone = new THREE.Mesh(new THREE.ConeGeometry(1, 1, 20), measureAccentMat);
        cone.renderOrder = 21;

        const dot = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 16), measureMat);
        dot.renderOrder = 22;

        const hit = new THREE.Mesh(new THREE.SphereGeometry(1, 12, 12), measureHitMat);
        hit.userData.idx = i;

        measureGroup.add(coneHalo, dotHalo, cone, dot, hit);
        measureCones.push(cone);
        measureDots.push(dot);
        measureHits.push(hit);
        measureConeHalos.push(coneHalo);
        measureDotHalos.push(dotHalo);
      }

      if (meshesRef.current.length > 0) {
        meshesRef.current[0].add(measureGroup);
      } else {
        scene.add(measureGroup);
      }
    };

    const pickSurfacePoint = (clientX, clientY) => {
      const pickable = visMeshes();
      if (pickable.length === 0) return null;
      updatePointerNDC(clientX, clientY);
      raycaster.setFromCamera(pointerNDC, camera);

      const parent = measureGroup?.parent || meshesRef.current[0];
      meshesRef.current.forEach((m) => m.updateMatrixWorld(true));

      const hits = raycaster.intersectObjects(pickable, false);
      for (let i = 0; i < hits.length; i++) {
        if (clipEnabledRef.current && clipPlane.distanceToPoint(hits[i].point) < 0) continue;
        return parent.worldToLocal(hits[i].point.clone());
      }
      return null;
    };

    const pickMeasureTip = (clientX, clientY) => {
      if (!measureGroup || measurePts.length === 0) return -1;
      updatePointerNDC(clientX, clientY);
      raycaster.setFromCamera(pointerNDC, camera);
      const parentMesh = measureGroup.parent;
      if (parentMesh) parentMesh.updateMatrixWorld(true);
      const hits = raycaster.intersectObjects(
        measureHits.slice(0, measurePts.length),
        false
      );
      return hits.length ? hits[0].object.userData.idx : -1;
    };

    const worldPerPixelAt = (localPt) => {
      const parentMesh = measureGroup?.parent || meshesRef.current[0];
      if (!parentMesh) return 0.01;
      mTmpW.copy(localPt);
      parentMesh.localToWorld(mTmpW);
      const d = camera.position.distanceTo(mTmpW);
      const hpx = Math.max(renderer.domElement.clientHeight, 1);
      return (2 * d * Math.tan((camera.fov * Math.PI) / 360)) / hpx;
    };

    const updateMeasureOverlay = () => {
      const label = measureLabelRef.current;
      const n = measurePts.length;
      const active = measureActiveRef.current && n > 0 && meshesRef.current.length > 0 && !!measureGroup;

      if (measureGroup) measureGroup.visible = active;
      if (!active) {
        if (label) label.style.display = "none";
        return;
      }

      const parentMesh = measureGroup.parent;
      if (parentMesh) parentMesh.updateMatrixWorld(true);

      if (parentMesh && parentMesh.visible === false) {
        if (label) label.style.display = "none";
        return;
      }

      for (let i = 0; i < 2; i++) {
        const has = i < n;
        measureDots[i].visible = has;
        measureDotHalos[i].visible = has;
        measureHits[i].visible = has;
        if (!has) continue;

        const ppx = worldPerPixelAt(measurePts[i]);
        measureDots[i].position.copy(measurePts[i]);
        measureDots[i].scale.setScalar(MEASURE_DOT_PX * ppx);

        measureDotHalos[i].position.copy(measurePts[i]);
        measureDotHalos[i].scale.setScalar((MEASURE_DOT_PX + MEASURE_HALO_PX) * ppx);

        measureHits[i].position.copy(measurePts[i]);
        measureHits[i].scale.setScalar(MEASURE_HIT_PX * ppx);
      }

      let showTape = false;
      let dist = 0;

      if (n === 2) {
        const A = measurePts[0];
        const B = measurePts[1];
        mDir.subVectors(B, A);
        dist = mDir.length();
        showTape = dist > 1e-9;

        if (showTape) {
          mDir.divideScalar(dist);
          mMid.addVectors(A, B).multiplyScalar(0.5);

          const ppxMid = worldPerPixelAt(mMid);

          measureTube.position.copy(mMid);
          measureTube.quaternion.setFromUnitVectors(Y_AXIS, mDir);
          measureTube.scale.set(
            MEASURE_TUBE_PX * ppxMid,
            dist,
            MEASURE_TUBE_PX * ppxMid
          );

          measureTubeHalo.position.copy(mMid);
          measureTubeHalo.quaternion.copy(measureTube.quaternion);
          measureTubeHalo.scale.set(
            (MEASURE_TUBE_PX + MEASURE_HALO_PX) * ppxMid,
            dist,
            (MEASURE_TUBE_PX + MEASURE_HALO_PX) * ppxMid
          );

          for (let i = 0; i < 2; i++) {
            const P = measurePts[i];
            const ppx = worldPerPixelAt(P);
            const cl = Math.min(MEASURE_CONE_LENGTH_PX * ppx, dist * 0.35);
            const cr = MEASURE_CONE_RADIUS_PX * ppx;
            const sgn = i === 1 ? 1 : -1;

            const cone = measureCones[i];
            cone.quaternion.setFromUnitVectors(
              Y_AXIS,
              mTmpW.copy(mDir).multiplyScalar(sgn)
            );
            cone.position.copy(P).addScaledVector(mDir, -sgn * (cl / 2));
            cone.scale.set(cr, cl, cr);

            const hp = MEASURE_HALO_PX * ppx;
            const ch = measureConeHalos[i];
            ch.quaternion.copy(cone.quaternion);
            ch.position.copy(cone.position);
            ch.scale.set(cr + hp, cl + hp * 2, cr + hp);
          }
        }
      }

      measureTube.visible = showTape;
      measureTubeHalo.visible = showTape;
      measureCones[0].visible = showTape;
      measureCones[1].visible = showTape;
      measureConeHalos[0].visible = showTape;
      measureConeHalos[1].visible = showTape;

      if (!label) return;
      if (!showTape) {
        label.style.display = "none";
        return;
      }

      mMid.addVectors(measurePts[0], measurePts[1]).multiplyScalar(0.5);
      if (parentMesh) {
        parentMesh.localToWorld(mMid);
      }
      mMid.project(camera);

      if (mMid.z > 1 || mMid.z < -1) {
        label.style.display = "none";
        return;
      }

      const w = renderer.domElement.clientWidth;
      const h = renderer.domElement.clientHeight;
      const x = (mMid.x * 0.5 + 0.5) * w;
      const y = (-mMid.y * 0.5 + 0.5) * h;

      label.textContent = formatMm(dist) + " " + MEASURE_UNIT;
      label.style.display = "block";
      label.style.transform =
        "translate(" + x.toFixed(1) + "px, " + y.toFixed(1) + "px) translate(-50%, -150%)";
    };

    measureApiRef.current = {
      setActive: (on) => {
        measureActiveRef.current = on;
        if (!on) {
          measurePts.length = 0;
          measureDrag = null;
        }
        needsRender = true;
        schedulePersist();
      },
      clear: () => {
        measurePts.length = 0;
        needsRender = true;
        schedulePersist();
      },
    };

    const pickGizmo = (clientX, clientY) => {
      if (!sectionActiveRef.current || !clipPlaneGroup || !clipPlaneGroup.visible) {
        return null;
      }
      updatePointerNDC(clientX, clientY);
      raycaster.setFromCamera(pointerNDC, camera);
      clipPlaneGroup.updateMatrixWorld(true);
      const hits = raycaster.intersectObjects(gizmoPickables, false);
      if (!hits.length) return null;
      const handleHit = hits.find((h) => h.object.userData.handleId !== "disc");
      return (handleHit || hits[0]).object.userData.handleId;
    };

    const startDrag = (id, ev) => {
      const n0 = planeNormal.clone();
      const u0 = planeUp.clone();
      const r0 = new THREE.Vector3().crossVectors(u0, n0).normalize();
      const s0 = planeOffsetRef.current;
      const sizeMult0 = planeSizeMultRef.current;
      const shift0 = planeShift.clone();

      drag = {
        id,
        startX: ev.clientX,
        startY: ev.clientY,
        n0, u0, r0, s0,
        sizeMult0,
        shift0,
      };

      if (id === "translate" || id === "disc") {
        drag.kind = "translate";
        drag.C0 = n0
          .clone()
          .multiplyScalar(s0 * radius * CLIP_OFFSET_RANGE)
          .add(shift0);
        updatePointerNDC(ev.clientX, ev.clientY);
        raycaster.setFromCamera(pointerNDC, camera);
        drag.t0 = closestParamOnLine(raycaster.ray, drag.C0, n0);
      } else if (id === "size") {
        drag.kind = "size";
        const size = radius * sizeMult0;
        const handleLocalPos = new THREE.Vector3(size, 0, 0);
        const centerWorld = n0
          .clone()
          .multiplyScalar(s0 * radius * CLIP_OFFSET_RANGE)
          .add(shift0);

        const rightW = new THREE.Vector3().crossVectors(u0, n0).normalize();
        const m = new THREE.Matrix4().makeBasis(rightW, u0, n0);

        const handleWorld = handleLocalPos.clone().applyMatrix4(m).add(centerWorld);
        drag.handleScreen0 = toScreenPx(handleWorld);
        drag.centerScreen0 = toScreenPx(centerWorld);
        drag.startDist0 = drag.handleScreen0.distanceTo(drag.centerScreen0);
      } else {
        const def = ROT_HANDLES.find((h) => h.id === id);
        if (!def) { drag = null; return; }
        drag.kind = "rotate";
        const toWorld = (a) =>
          new THREE.Vector3()
            .addScaledVector(r0, a[0])
            .addScaledVector(u0, a[1])
            .addScaledVector(n0, a[2]);
        const axisW = toWorld(def.axis).normalize();
        const radialW = toWorld(def.radial).normalize();
        const size = radius * sizeMult0;
        const handlePos = n0
          .clone()
          .multiplyScalar(s0 * radius * CLIP_OFFSET_RANGE)
          .add(shift0)
          .addScaledVector(radialW, size);

        camera.updateMatrixWorld();
        const delta = 0.05;
        const p1 = toScreenPx(handlePos);
        const p2 = toScreenPx(handlePos.clone().applyAxisAngle(axisW, delta));
        const diff = p2.clone().sub(p1);
        drag.axisW = axisW;
        drag.pxPerRad = Math.max(diff.length() / delta, 40);
        drag.sdir = diff.length() > 1e-6 ? diff.normalize() : new THREE.Vector2(1, 0);
      }

      renderer.domElement.style.cursor = "grabbing";
      setGizmoHighlight(true, id);
      window.addEventListener("pointermove", onWindowMove);
      window.addEventListener("pointerup", onWindowUp);
      window.addEventListener("pointercancel", onWindowUp);
    };

    const updateDrag = (ev) => {
      if (!drag) return;

      if (drag.kind === "translate") {
        updatePointerNDC(ev.clientX, ev.clientY);
        raycaster.setFromCamera(pointerNDC, camera);
        const t = closestParamOnLine(raycaster.ray, drag.C0, drag.n0);
        let delta;
        if (t !== null && drag.t0 !== null) {
          delta = t - drag.t0;
        } else {
          const rect = renderer.domElement.getBoundingClientRect();
          delta = (drag.startY - ev.clientY) * ((radius * 2) / Math.max(rect.height, 1));
        }
        const newS = THREE.MathUtils.clamp(
          drag.s0 + delta / (radius * CLIP_OFFSET_RANGE),
          -1, 1
        );
        const v = +newS.toFixed(3);
        planeOffsetRef.current = v;
        applyPlaneTransform();
        scheduleCrossSectionUpdate(30);
        setPlaneOffset(v);
        return;
      }

      if (drag.kind === "size") {
        const rect = renderer.domElement.getBoundingClientRect();
        const sx = ev.clientX - rect.left;
        const sy = ev.clientY - rect.top;
        const c = drag.centerScreen0;
        const newDist = Math.hypot(sx - c.x, sy - c.y);
        if (drag.startDist0 > 4) {
          const ratio = newDist / drag.startDist0;
          const newMult = THREE.MathUtils.clamp(
            drag.sizeMult0 * ratio,
            CLIP_PLANE_SIZE_MULT_MIN,
            CLIP_PLANE_SIZE_MULT_MAX
          );
          if (Math.abs(newMult - planeSizeMultRef.current) > 1e-4) {
            planeSizeMultRef.current = newMult;
            if (clipPlaneGroup && meshesRef.current.length > 0) {
              buildClipPlaneVisual();
              if (clipPlaneGroup) clipPlaneGroup.visible = sectionActiveRef.current;
              applyPlaneTransform();
            }
            setPlaneSizeMult(newMult);
          }
        }
        return;
      }

      if (drag.kind === "rotate") {
        const dx = ev.clientX - drag.startX;
        const dy = ev.clientY - drag.startY;
        const angle = (dx * drag.sdir.x + dy * drag.sdir.y) / drag.pxPerRad;
        const q = new THREE.Quaternion().setFromAxisAngle(drag.axisW, angle);
        planeNormal.copy(drag.n0).applyQuaternion(q);
        planeUp.copy(drag.u0).applyQuaternion(q);
        planeShift.copy(drag.shift0).applyQuaternion(q);
        applyPlaneTransform();
        scheduleCrossSectionUpdate(40);
      }
    };

    function onWindowMove(ev) {
      if (!drag) return;
      updateDrag(ev);
    }

    function onWindowUp() {
      if (!drag) return;
      drag = null;
      setGizmoHighlight(false);
      window.removeEventListener("pointermove", onWindowMove);
      window.removeEventListener("pointerup", onWindowUp);
      window.removeEventListener("pointercancel", onWindowUp);
      renderer.domElement.style.cursor = "default";
      if (sectionActiveRef.current) scheduleCrossSectionUpdate(0);
      schedulePersist();
    }

    function onMeasureMove(ev) {
      if (measureDrag === null) return;
      const p = pickSurfacePoint(ev.clientX, ev.clientY);
      if (p) {
        measurePts[measureDrag].copy(p);
        needsRender = true;
      }
    }

    function onMeasureUp() {
      if (measureDrag === null) return;
      measureDrag = null;
      window.removeEventListener("pointermove", onMeasureMove);
      window.removeEventListener("pointerup", onMeasureUp);
      window.removeEventListener("pointercancel", onMeasureUp);
      renderer.domElement.style.cursor = "crosshair";
      schedulePersist();
    }

    const handleMeasureDown = (ev) => {
      if (meshesRef.current.length === 0 || !measureGroup) return false;

      const tip = pickMeasureTip(ev.clientX, ev.clientY);
      if (tip >= 0) {
        measureDrag = tip;
        renderer.domElement.style.cursor = "grabbing";
        window.addEventListener("pointermove", onMeasureMove);
        window.addEventListener("pointerup", onMeasureUp);
        window.addEventListener("pointercancel", onMeasureUp);
        return true;
      }

      const p = pickSurfacePoint(ev.clientX, ev.clientY);
      if (!p) return false;
      if (measurePts.length >= 2) measurePts.length = 0;
      measurePts.push(p);
      needsRender = true;
      schedulePersist();
      return true;
    };

    let selectedMesh = null;
    let modelDrag = null;

    const applySelectedLook = (mesh) => {
      const mat = mesh?.material;
      if (!mat) return;

      if (!mesh.userData.originalMaterialLook) {
        mesh.userData.originalMaterialLook = {
          color: mat.color ? mat.color.getHex() : null,
          emissive: mat.emissive ? mat.emissive.getHex() : null,
          emissiveIntensity:
            typeof mat.emissiveIntensity === "number"
              ? mat.emissiveIntensity
              : null,
          metalness:
            typeof mat.metalness === "number" ? mat.metalness : null,
          roughness:
            typeof mat.roughness === "number" ? mat.roughness : null,
        };
      }

      if (mat.color && !mat.vertexColors) {
        mat.color.setHex(MODEL_SELECTED_BASE_COLOR);
      }

      if (mat.emissive) mat.emissive.setHex(MODEL_SELECTED_EMISSIVE);
      if (typeof mat.emissiveIntensity === "number") {
        mat.emissiveIntensity = MODEL_SELECTED_EMISSIVE_INTENSITY;
      }
      if (typeof mat.metalness === "number") {
        mat.metalness = MODEL_SELECTED_METALNESS;
      }
      if (typeof mat.roughness === "number") {
        mat.roughness = MODEL_SELECTED_ROUGHNESS;
      }
      mat.needsUpdate = true;
    };

    const restoreOriginalLook = (mesh) => {
      const mat = mesh?.material;
      const orig = mesh?.userData?.originalMaterialLook;
      if (!mat || !orig) return;

      if (mat.color && orig.color !== null) mat.color.setHex(orig.color);
      if (mat.emissive && orig.emissive !== null) mat.emissive.setHex(orig.emissive);
      if (typeof mat.emissiveIntensity === "number" && orig.emissiveIntensity !== null) {
        mat.emissiveIntensity = orig.emissiveIntensity;
      }
      if (typeof mat.metalness === "number" && orig.metalness !== null) {
        mat.metalness = orig.metalness;
      }
      if (typeof mat.roughness === "number" && orig.roughness !== null) {
        mat.roughness = orig.roughness;
      }
      mat.needsUpdate = true;
    };

    const getGroupOf = (m) => {
      if (!m) return [];
      const gid = m.userData.groupId;
      if (gid === undefined || m.userData.detached) return [m];
      return meshesRef.current.filter(
        (x) => x.userData.groupId === gid && !x.userData.detached
      );
    };

    const groupPivot = (members) => {
      const c = new THREE.Vector3();
      members.forEach((x) => c.add(x.position));
      return c.divideScalar(Math.max(members.length, 1));
    };

    const rotateMembersAround = (members, q, pivot) => {
      members.forEach((x) => {
        x.position.sub(pivot).applyQuaternion(q).add(pivot);
        x.quaternion.premultiply(q);
      });
    };

    const setModelDetached = (fileIndex, on) => {
      const mesh = meshesRef.current.find(
        (m) => m.userData.fileIndex === fileIndex
      );
      if (!mesh || mesh.userData.groupId === undefined) return;
      const gid = mesh.userData.groupId;
      const others = meshesRef.current.filter(
        (x) => x !== mesh && x.userData.groupId === gid && !x.userData.detached
      );
      if (others.length === 0) return;

      if (on) {
        if (mesh.userData.detached) return;

        const gBox = new THREE.Box3();
        others.forEach((x) => {
          x.updateMatrixWorld(true);
          gBox.union(new THREE.Box3().setFromObject(x));
        });
        mesh.updateMatrixWorld(true);
        const mBox = new THREE.Box3().setFromObject(mesh);

        const gSize = gBox.getSize(new THREE.Vector3());
        const mSize = mBox.getSize(new THREE.Vector3());
        const gCenter = gBox.getCenter(new THREE.Vector3());
        const mCenter = mBox.getCenter(new THREE.Vector3());

        const gap =
          Math.max(gSize.x, gSize.y, gSize.z, mSize.x, mSize.y, mSize.z) *
          MULTI_MODEL_GAP_FACTOR;
        const side = mCenter.x - gCenter.x < 0 ? -1 : 1;
        const targetX =
          side > 0
            ? gBox.max.x + gap + mSize.x / 2
            : gBox.min.x - gap - mSize.x / 2;

        mesh.position.add(
          new THREE.Vector3(targetX - mCenter.x, gCenter.y - mCenter.y, gCenter.z - mCenter.z)
        );
        mesh.userData.detached = true;
      } else {
        if (!mesh.userData.detached) return;
        const ref = others[0];
        const refHomeQ = ref.userData.homeQuat || new THREE.Quaternion();
        const refHomeP = ref.userData.homePos || ref.position.clone();
        const homeQ = mesh.userData.homeQuat || new THREE.Quaternion();
        const homeP = mesh.userData.homePos || mesh.position.clone();

        const R = ref.quaternion.clone().multiply(refHomeQ.clone().invert());
        mesh.quaternion.copy(R).multiply(homeQ);
        mesh.position
          .copy(homeP)
          .sub(refHomeP)
          .applyQuaternion(R)
          .add(ref.position);
        mesh.userData.detached = false;
      }

      mesh.updateMatrixWorld(true);
      if (sectionActiveRef.current) scheduleCrossSectionUpdate(0);
      needsRender = true;
    };
    if (viewApiRef.current) viewApiRef.current.setDetached = setModelDetached;

    let selectedGroup = [];
    const setSelectedMesh = (m) => {
      if (selectedMesh === m) return;
      selectedGroup.forEach((x) => restoreOriginalLook(x));
      selectedMesh = m;
      selectedGroup = m ? getGroupOf(m) : [];
      selectedGroup.forEach((x) => applySelectedLook(x));
      needsRender = true;
    };

    const pickModel = (clientX, clientY) => {
      const pickable = visMeshes();
      if (pickable.length === 0) return null;
      updatePointerNDC(clientX, clientY);
      raycaster.setFromCamera(pointerNDC, camera);
      meshesRef.current.forEach((m) => m.updateMatrixWorld(true));
      const hits = raycaster.intersectObjects(pickable, false);
      for (let i = 0; i < hits.length; i++) {
        if (clipEnabledRef.current && clipPlane.distanceToPoint(hits[i].point) < 0) continue;
        return hits[i];
      }

      const rect = renderer.domElement.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const tanHalf = Math.tan((camera.fov * Math.PI) / 360);
      let best = null;
      let bestD = Infinity;
      pickable.forEach((m) => {
        const bs = m.geometry.boundingSphere;
        if (!bs) return;
        const c = m.position;
        const dist = camera.position.distanceTo(c);
        if (dist <= 0) return;
        const sp = toScreenPx(c);
        const pxR = (bs.radius / (dist * tanHalf)) * (rect.height / 2);
        const d = Math.hypot(sp.x - px, sp.y - py);
        if (d <= pxR && d < bestD) {
          bestD = d;
          best = m;
        }
      });
      return best ? { object: best, point: best.position.clone() } : null;
    };

    const getPlaneCenter = () =>
      planeNormal
        .clone()
        .multiplyScalar(planeOffsetRef.current * radius * CLIP_OFFSET_RANGE)
        .add(planeShift);

    const setPlaneFromCenter = (n, u, C) => {
      planeNormal.copy(n).normalize();
      planeUp.copy(u);
      const sW = planeNormal.dot(C);
      const off = THREE.MathUtils.clamp(sW / (radius * CLIP_OFFSET_RANGE), -1, 1);
      planeOffsetRef.current = off;
      planeShift.copy(C).sub(planeNormal.clone().multiplyScalar(sW));
      applyPlaneTransform();
      setPlaneOffset(+off.toFixed(3));
    };

    const ensureSectionOwner = () => {
      if (
        sectionOwner &&
        meshesRef.current.includes(sectionOwner) &&
        sectionOwner.visible
      ) {
        return sectionOwner;
      }
      const C = getPlaneCenter();
      let best = null;
      let bestD = Infinity;
      visMeshes().forEach((m) => {
        const d = m.position.distanceTo(C);
        if (d < bestD) {
          bestD = d;
          best = m;
        }
      });
      sectionOwner = best;
      return sectionOwner;
    };

    const translatePlaneWithModel = (mesh, delta) => {
      if (!sectionActiveRef.current || !clipPlaneGroup) return;
      if (mesh !== ensureSectionOwner()) return;
      if (delta.lengthSq() < 1e-14) return;
      const C = getPlaneCenter().add(delta);
      setPlaneFromCenter(planeNormal.clone(), planeUp.clone(), C);
    };

    function rotatePlaneWithModel(mesh, q, pivot) {
      if (!sectionActiveRef.current || !clipPlaneGroup) return;
      if (mesh !== ensureSectionOwner()) return;
      const n = planeNormal.clone().applyQuaternion(q);
      const u = planeUp.clone().applyQuaternion(q);
      const C = getPlaneCenter().sub(pivot).applyQuaternion(q).add(pivot);
      setPlaneFromCenter(n, u, C);
    }

    function onModelMove(ev) {
      if (!modelDrag) return;
      const m = modelDrag.mesh;
      const members = modelDrag.members;

      if (modelDrag.mode === "move") {
        updatePointerNDC(ev.clientX, ev.clientY);
        raycaster.setFromCamera(pointerNDC, camera);
        const pt = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(modelDrag.plane, pt)) {
          const prevPos = m.position.clone();
          const offset = pt.sub(modelDrag.startPoint);
          members.forEach((x, i) => {
            x.position.copy(modelDrag.startPositions[i]).add(offset);
          });
          const delta = m.position.clone().sub(prevPos);
          members.forEach((x) => translatePlaneWithModel(x, delta));
        }
      } else {
        const dx = ev.clientX - modelDrag.lastX;
        const dy = ev.clientY - modelDrag.lastY;
        modelDrag.lastX = ev.clientX;
        modelDrag.lastY = ev.clientY;

        camera.updateMatrixWorld();
        const upAxis = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion).normalize();
        const rightAxis = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion).normalize();

        const qUp = new THREE.Quaternion().setFromAxisAngle(upAxis, dx * MODEL_ROTATE_SPEED);
        const qRight = new THREE.Quaternion().setFromAxisAngle(rightAxis, dy * MODEL_ROTATE_SPEED);
        const qStep = qRight.multiply(qUp);

        const pivot = modelDrag.pivot;
        rotateMembersAround(members, qStep, pivot);
        members.forEach((x) => rotatePlaneWithModel(x, qStep, pivot));
      }

      needsRender = true;
      if (sectionActiveRef.current) scheduleCrossSectionUpdate(40);
    }

    function onModelUp() {
      if (!modelDrag) return;
      modelDrag = null;
      controls.enabled = true;
      window.removeEventListener("pointermove", onModelMove);
      window.removeEventListener("pointerup", onModelUp);
      window.removeEventListener("pointercancel", onModelUp);
      renderer.domElement.style.cursor = "default";
      if (sectionActiveRef.current) scheduleCrossSectionUpdate(0);
      if (!isMulti) schedulePersist();
      needsRender = true;
    }

    const startModelDrag = (mode, mesh, hitPoint, ev) => {
      const camDir = new THREE.Vector3();
      camera.getWorldDirection(camDir);
      const dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, hitPoint);

      updatePointerNDC(ev.clientX, ev.clientY);
      raycaster.setFromCamera(pointerNDC, camera);
      const startPt = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(dragPlane, startPt)) startPt.copy(hitPoint);

      const members = ev.altKey ? [mesh] : getGroupOf(mesh);
      const pivot =
        members.length > 1 ? groupPivot(members) : mesh.position.clone();

      modelDrag = {
        mode,
        mesh,
        members,
        pivot,
        lastX: ev.clientX,
        lastY: ev.clientY,
        startPositions: members.map((x) => x.position.clone()),
        startPoint: startPt,
        plane: dragPlane,
      };

      controls.enabled = false;
      renderer.domElement.style.cursor = mode === "move" ? "move" : "grabbing";
      window.addEventListener("pointermove", onModelMove);
      window.addEventListener("pointerup", onModelUp);
      window.addEventListener("pointercancel", onModelUp);
    };

    const handleModelDown = (ev) => {
      if (sectionDrawModeRef.current) return false;

      if (!isMulti) {
        if (ev.button !== 2 || ev.shiftKey) return false;
        const m = meshesRef.current[0];
        if (!m) return false;
        startModelDrag("rotate", m, m.position, ev);
        return true;
      }

      if (meshesRef.current.length === 0) return false;

      const hit = pickModel(ev.clientX, ev.clientY);

      if (ev.button === 0) {
        if (measureActiveRef.current) return false;
        if (!hit) {
          setSelectedMesh(null);
          return false;
        }
        setSelectedMesh(hit.object);
        startModelDrag("move", hit.object, hit.point, ev);
        return true;
      }

      if (ev.button === 2) {
        if (ev.shiftKey) return false;
        const target = hit ? hit.object : selectedMesh;
        if (!target || !target.visible) return false;
        setSelectedMesh(target);
        startModelDrag("rotate", target, hit ? hit.point : target.position, ev);
        return true;
      }

      return false;
    };

    const onPointerDown = (ev) => {
      if (ev.button !== 0 && ev.button !== 2) return;
      const id = pickGizmo(ev.clientX, ev.clientY);
      const discRightPass = id === "disc" && ev.button === 2;

      if (id && !discRightPass) {
        startDrag(id, ev);
        ev.preventDefault();
        ev.stopPropagation();
        return;
      }

      if (ev.button === 0 && measureActiveRef.current) {
        if (handleMeasureDown(ev)) {
          ev.preventDefault();
          ev.stopPropagation();
        }
        return;
      }

      let handled = false;
      try {
        handled = handleModelDown(ev);
      } catch (err) {
        console.error("STLViewer: model drag failed", err);
        controls.enabled = true;
        modelDrag = null;
      }
      if (handled) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };

    const onPointerMoveHover = (ev) => {
      if (drag || measureDrag !== null || modelDrag) return;

      let cursor = "default";
      if (sectionActiveRef.current && clipPlaneGroup) {
        const id = pickGizmo(ev.clientX, ev.clientY);
        if (id) cursor = "grab";
      }
      if (cursor === "default" && measureActiveRef.current) {
        cursor = pickMeasureTip(ev.clientX, ev.clientY) >= 0 ? "grab" : "crosshair";
      }
      renderer.domElement.style.cursor = cursor;
    };

    const onCanvasContextMenu = (e) => {
      e.preventDefault();
    };

    if (!isSmall) {
      mount.addEventListener("pointerdown", onPointerDown, true);
      mount.addEventListener("contextmenu", onCanvasContextMenu, true);
      renderer.domElement.addEventListener("contextmenu", onCanvasContextMenu);
      renderer.domElement.addEventListener("pointermove", onPointerMoveHover);
    }

    // ================= BITE / OCCLUSION CHECK =================
    const biteState = {
      active: false,
      lower: null,
      upper: null,
      delta: new THREE.Vector3(),
    };
    const biteColor = new THREE.Color();

    const worldVertices = (mesh) => {
      mesh.updateMatrixWorld(true);
      const e = mesh.matrixWorld.elements;
      const pos = mesh.geometry.attributes.position;
      const n = pos.count;
      const out = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
        out[i * 3] = e[0] * x + e[4] * y + e[8] * z + e[12];
        out[i * 3 + 1] = e[1] * x + e[5] * y + e[9] * z + e[13];
        out[i * 3 + 2] = e[2] * x + e[6] * y + e[10] * z + e[14];
      }
      return out;
    };

    const restoreBiteLook = (mesh) => {
      const s = mesh && mesh.userData.biteSaved;
      if (!s || !mesh.material) return;
      if (s.color) mesh.geometry.setAttribute("color", s.color);
      else mesh.geometry.deleteAttribute("color");
      mesh.material.vertexColors = s.vertexColors;
      mesh.material.color.setHex(s.matColor);
      mesh.material.needsUpdate = true;
      delete mesh.userData.biteSaved;
    };

    const paintBite = (mesh, colors) => {
      if (!mesh.userData.biteSaved) {
        mesh.userData.biteSaved = {
          color: mesh.geometry.attributes.color
            ? mesh.geometry.attributes.color.clone()
            : null,
          vertexColors: mesh.material.vertexColors,
          matColor: mesh.material.color.getHex(),
        };
      }
      mesh.geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      mesh.material.vertexColors = true;
      mesh.material.color.setHex(0xffffff);
      mesh.material.needsUpdate = true;
    };

    const closeBite = () => {
      const vis = visMeshes();
      if (vis.length < 2) return null;

      // 1) the two jaws = the two visible models farthest apart
      const centers = vis.map((m) =>
        new THREE.Box3().setFromObject(m).getCenter(new THREE.Vector3())
      );
      let bi = 0, bj = 1, bd = -1;
      for (let i = 0; i < vis.length; i++) {
        for (let j = i + 1; j < vis.length; j++) {
          const d = centers[i].distanceTo(centers[j]);
          if (d > bd) { bd = d; bi = i; bj = j; }
        }
      }

      // 2) occlusal axis = dominant axis between the jaws (Y preferred)
      const diff = [
        centers[bj].x - centers[bi].x,
        centers[bj].y - centers[bi].y,
        centers[bj].z - centers[bi].z,
      ];
      let ai = 1;
      let best = Math.abs(diff[1]) * 1.5;
      [0, 2].forEach((k) => {
        if (Math.abs(diff[k]) > best) { ai = k; best = Math.abs(diff[k]); }
      });
      const upper = diff[ai] >= 0 ? vis[bj] : vis[bi];
      const lower = upper === vis[bj] ? vis[bi] : vis[bj];
      const ui = (ai + 1) % 3;
      const vi = (ai + 2) % 3;

      // 3) height maps
      const WU = worldVertices(upper);
      const WL = worldVertices(lower);

      let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
      [WU, WL].forEach((W) => {
        for (let i = 0; i < W.length; i += 3) {
          const a = W[i + ui], b = W[i + vi];
          if (a < minU) minU = a;
          if (a > maxU) maxU = a;
          if (b < minV) minV = b;
          if (b > maxV) maxV = b;
        }
      });
      const cell = Math.max(maxU - minU, maxV - minV, 1e-6) / BITE_GRID;
      const nu = Math.floor((maxU - minU) / cell) + 1;
      const nv = Math.floor((maxV - minV) / cell) + 1;
      const total = nu * nv;

      const cellOf = (W, i) => {
        const cu = Math.min(nu - 1, Math.floor((W[i + ui] - minU) / cell));
        const cv = Math.min(nv - 1, Math.floor((W[i + vi] - minV) / cell));
        return cv * nu + cu;
      };

      const upMin = new Float32Array(total).fill(Infinity);
      const loMax = new Float32Array(total).fill(-Infinity);
      const upCnt = new Uint16Array(total);
      const loCnt = new Uint16Array(total);

      for (let i = 0; i < WU.length; i += 3) {
        const c = cellOf(WU, i);
        const h = WU[i + ai];
        if (h < upMin[c]) upMin[c] = h;
        if (upCnt[c] < 65535) upCnt[c]++;
      }
      for (let i = 0; i < WL.length; i += 3) {
        const c = cellOf(WL, i);
        const h = WL[i + ai];
        if (h > loMax[c]) loMax[c] = h;
        if (loCnt[c] < 65535) loCnt[c]++;
      }

      // 4) distance until first contact
      let minGap = Infinity;
      for (let c = 0; c < total; c++) {
        if (upCnt[c] >= BITE_MIN_SAMPLES && loCnt[c] >= BITE_MIN_SAMPLES) {
          const g = upMin[c] - loMax[c];
          if (g < minGap) minGap = g;
        }
      }
      if (!Number.isFinite(minGap)) return null; // jaws don't overlap

      let contactCells = 0;
      for (let c = 0; c < total; c++) {
        if (upCnt[c] >= BITE_MIN_SAMPLES && loCnt[c] >= BITE_MIN_SAMPLES) {
          if (upMin[c] - loMax[c] - minGap < BITE_CONTACT_MM) contactCells++;
        }
      }

      // 5) close the jaw (move lower up to first contact)
      const delta = new THREE.Vector3();
      delta.setComponent(ai, minGap);
      lower.position.add(delta);
      lower.updateMatrixWorld(true);

      // 6) contact heat-map on both jaws
      const paint = (mesh, W, isUpper) => {
        const n = W.length / 3;
        const colors = new Float32Array(n * 3);
        for (let k = 0; k < n; k++) {
          const i = k * 3;
          const c = cellOf(W, i);
          const h = W[i + ai];
          let g = Infinity;
          if (isUpper) {
            if (loCnt[c] >= BITE_MIN_SAMPLES) g = h - (loMax[c] + minGap);
          } else if (upCnt[c] >= BITE_MIN_SAMPLES) {
            g = upMin[c] - (h + minGap);
          }
          if (g < BITE_MAX_MM) {
            biteColor.setHSL(0.66 * (Math.max(0, g) / BITE_MAX_MM), 0.9, 0.5);
            colors[i] = biteColor.r;
            colors[i + 1] = biteColor.g;
            colors[i + 2] = biteColor.b;
          } else {
            colors[i] = 0.91;
            colors[i + 1] = 0.91;
            colors[i + 2] = 0.91;
          }
        }
        paintBite(mesh, colors);
      };
      paint(upper, WU, true);
      paint(lower, WL, false);

      biteState.lower = lower;
      biteState.upper = upper;
      biteState.delta.copy(delta);

      return { minGap, area: contactCells * cell * cell };
    };

    biteApiRef.current = {
      setActive: (on) => {
        if (on === biteState.active) return;
        if (on) {
          let res = null;
          try {
            res = closeBite();
          } catch (err) {
            console.error("STLViewer: bite check failed", err);
          }
          if (!res) {
            setBiteMode(false);
            return;
          }
          biteState.active = true;
          setBiteInfo({ closed: res.minGap, area: res.area });
        } else {
          if (biteState.lower) biteState.lower.position.sub(biteState.delta);
          restoreBiteLook(biteState.lower);
          restoreBiteLook(biteState.upper);
          biteState.active = false;
          biteState.lower = null;
          biteState.upper = null;
          biteState.delta.set(0, 0, 0);
          setBiteInfo(null);
        }
        needsRender = true;
        if (sectionActiveRef.current) scheduleCrossSectionUpdate(0);
      },
    };

    const animate = (time = 0) => {
      if (disposed) return;
      frameId = requestAnimationFrame(animate);

      const deltaSeconds = prevTime
        ? Math.min((time - prevTime) / 1000, 0.05)
        : 0;
      prevTime = time;

      if (!isSmall) {
        if (controls.enabled) controls.update();
        constrainView();
      } else if (autoRotateRef.current && meshesRef.current.length > 0) {
        controls.update();
      }

      if (autoRotateRef.current && meshesRef.current.length > 0) {
        const autoTargets =
          isMulti && selectedMesh ? getGroupOf(selectedMesh) : meshesRef.current;
        const angle = deltaSeconds * 0.35;
        const doneGroups = new Set();
        autoTargets.forEach((m) => {
          const gid = m.userData.groupId;
          if (gid === undefined || m.userData.detached) {
            m.rotation.y += angle;
            return;
          }
          if (doneGroups.has(gid)) return;
          doneGroups.add(gid);
          const members = getGroupOf(m);
          if (members.length < 2) {
            m.rotation.y += angle;
            return;
          }
          const qY = new THREE.Quaternion().setFromAxisAngle(Y_AXIS, angle);
          rotateMembersAround(members, qY, groupPivot(members));
        });
        needsRender = true;
      }

      if (needsRender) {
        if (!isSmall) {
          updateMeasureOverlay();
          if (drawState) updateDrawVisual();
        }
        renderer.render(scene, camera);
        needsRender = false;
      }
    };

    // ✅ NEW: screenshot - render one fresh frame and copy it right away
    // (the WebGL buffer is only readable in the same tick as the render)
    if (viewApiRef.current) {
      viewApiRef.current.captureCanvas = (background) => {
        if (disposed || isSmall) return null;
        updateMeasureOverlay();
        if (drawState) updateDrawVisual();
        renderer.render(scene, camera);

        const src = renderer.domElement;
        const out = document.createElement("canvas");
        out.width = src.width;
        out.height = src.height;
        const ctx = out.getContext("2d");
        ctx.fillStyle = background || "#00a1d6";
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(src, 0, 0);
        return out;
      };
    }

    const setupSingleMesh = (loadedGeometry, colorOverride, applyFit, fileIndex) => {
      if (disposed) { loadedGeometry.dispose(); return null; }

      let geometry = loadedGeometry;

      const hasVertexColors = !!geometry.attributes.color;

      try {
        const count = geometry.attributes.position.count;
        const triCount = geometry.index
          ? geometry.index.count / 3
          : count / 3;

        if (count < SMOOTH_VERTEX_LIMIT && triCount < MERGE_VERTICES_MAX_TRIANGLES) {
          if (hasVertexColors) {
            geometry.computeVertexNormals();
          } else {
            if (geometry.attributes.normal) geometry.deleteAttribute("normal");
            geometry = mergeVertices(geometry, 1e-4);
            geometry.computeVertexNormals();
          }
        } else {
          if (!geometry.attributes.normal) {
            geometry.computeVertexNormals();
          }
        }
      } catch (e) {
        console.warn("Smooth normals skipped:", e);
        geometry = loadedGeometry;
      }

      geometry.computeBoundingBox();
      const origCenter = new THREE.Vector3();
      if (geometry.boundingBox) geometry.boundingBox.getCenter(origCenter);

      geometry.center();
      geometry.computeBoundingSphere();

      const triCount = geometry.index
        ? geometry.index.count / 3
        : geometry.attributes.position.count / 3;
      if (!isSmall && triCount > HEAVY_TRIANGLE_COUNT) isHeavy = true;

      const currentOpacity = opacityRef.current;
      const isTransparent = currentOpacity < 1;

      const clippingPlanes = clipEnabledRef.current ? [clipPlane] : [];

      const useVertexColors = !!geometry.attributes.color;

      const material = new THREE.MeshStandardMaterial({
        color: useVertexColors
          ? 0xffffff
          : (colorOverride !== undefined ? colorOverride : 0xe8e8e8),
        metalness: useVertexColors ? 0.1 : 0.05,
        roughness: useVertexColors ? 0.45 : 0.65,
        side: THREE.DoubleSide,
        wireframe: wireframeRef.current,
        transparent: isTransparent,
        opacity: currentOpacity,
        depthWrite: !isTransparent,
        clippingPlanes,
        clipShadows: false,
        clipIntersection: false,
        vertexColors: useVertexColors,
      });

      const mesh = new THREE.Mesh(geometry, material);

      mesh.userData.originalMaterialLook = {
        color: material.color.getHex(),
        emissive: material.emissive.getHex(),
        emissiveIntensity: material.emissiveIntensity,
        metalness: material.metalness,
        roughness: material.roughness,
      };
      mesh.userData.origCenter = origCenter;
      if (typeof fileIndex === "number") mesh.userData.fileIndex = fileIndex;

      scene.add(mesh);
      meshesRef.current.push(mesh);

      if (applyFit) {
        materialRef.current = material;
        meshRef.current = mesh;

        let maxR = 0;
        meshesRef.current.forEach((m) => {
          const r = m.geometry.boundingSphere?.radius || 1;
          maxR = Math.max(maxR, r);
        });
        radius = maxR || 1;

        const tangentDistance = computeTangentDistance();
        fitDistance = tangentDistance * INITIAL_FIT_FACTOR;
        maxDistanceLimit =
          tangentDistance * ZOOM_OUT_BASE_FACTOR * MAX_ZOOM_OUT_FACTOR;

        controls.minDistance = Math.max(radius * 0.02, 0.001);
        controls.maxDistance = Math.max(radius * 20, maxDistanceLimit);

        if (!isSmall) buildMeasureVisual();
        buildClipPlaneVisual();
        if (clipPlaneGroup) clipPlaneGroup.visible = sectionActiveRef.current;

        setView("default");

        try {
          const saved = !isSmall
            ? loadViewerState(isMulti ? fileUrls[0]?.url : fileUrl)
            : null;
          if (saved) {
            if (
              !isMulti &&
              saved.meshQuat &&
              Number.isFinite(saved.meshQuat.w)
            ) {
              mesh.quaternion.set(
                saved.meshQuat.x,
                saved.meshQuat.y,
                saved.meshQuat.z,
                saved.meshQuat.w
              );
              mesh.updateMatrixWorld(true);
            }

            if (saved.planeNormal && saved.planeUp) {
              planeNormal.set(saved.planeNormal.x, saved.planeNormal.y, saved.planeNormal.z);
              planeUp.set(saved.planeUp.x, saved.planeUp.y, saved.planeUp.z);
            }
            if (saved.planeShift) {
              planeShift.set(saved.planeShift.x, saved.planeShift.y, saved.planeShift.z);
            }
            if (typeof saved.planeOffset === "number") {
              planeOffsetRef.current = saved.planeOffset;
            }
            if (typeof saved.planeSizeMult === "number") {
              planeSizeMultRef.current = saved.planeSizeMult;
              if (clipPlaneGroup) {
                buildClipPlaneVisual();
                if (clipPlaneGroup) clipPlaneGroup.visible = sectionActiveRef.current;
              }
            }
            applyPlaneTransform();

            if (saved.camera && saved.target) {
              const c = saved.camera;
              const t = saved.target;
              camera.up.set(
                saved.up?.x ?? 0,
                saved.up?.y ?? 1,
                saved.up?.z ?? 0
              );
              camera.position.set(c.x, c.y, c.z);
              controls.target.set(t.x, t.y, t.z);
              camera.lookAt(controls.target);
              controls.update();
              updateClipping();
            }

            if (Array.isArray(saved.measurePts) && saved.measurePts.length > 0 && measureGroup) {
              measurePts.length = 0;
              for (const p of saved.measurePts) {
                measurePts.push(new THREE.Vector3(p.x, p.y, p.z));
              }
            }
          }
        } catch (e) {
          console.warn("STLViewer: could not restore state:", e);
        }

        prevTime = 0;
        animate();
        setReady(true);

        if (sectionActiveRef.current) scheduleCrossSectionUpdate(50);
      }

      return mesh;
    };

    const handleLoadError = (err) => {
      if (disposed) return;
      console.error("3D model load failed:", err);
      if (callbacksRef.current.onError) callbacksRef.current.onError(err);
    };

    const handleProgress = (xhr) => {
      if (disposed) return;
      if (xhr.lengthComputable && callbacksRef.current.onProgress) {
        callbacksRef.current.onProgress(Math.round((xhr.loaded / xhr.total) * 100));
      }
    };

    const loadSingle = (url, colorOverride, applyFit, onDone, fileIndex) => {
      const loaderDone = (geom) => {
        if (disposed) { geom.dispose(); return; }
        setupSingleMesh(geom, colorOverride, applyFit, fileIndex);
        if (onDone) onDone();
      };

      if (isPlyUrl(url)) {
        const plyLoader = new PLYLoader();
        plyLoader.load(url, loaderDone, handleProgress, handleLoadError);
      } else {
        const stlLoader = new STLLoader();
        stlLoader.load(url, loaderDone, handleProgress, handleLoadError);
      }
    };

    // ⚡ FIX: Keep models in their ORIGINAL relative positions (thumbnail-like)
    const layoutMultiModels = () => {
      const ordered = meshesRef.current
        .slice()
        .sort((a, b) => (a.userData.fileIndex ?? 0) - (b.userData.fileIndex ?? 0));

      const infos = ordered.map((m) => {
        m.geometry.computeBoundingBox();
        const size = new THREE.Vector3();
        m.geometry.boundingBox.getSize(size);
        const oc = m.userData.origCenter ? m.userData.origCenter.clone() : new THREE.Vector3();
        const half = size.clone().multiplyScalar(0.5);
        return {
          mesh: m,
          size,
          oc,
          min: oc.clone().sub(half),
          max: oc.clone().add(half),
        };
      });

      let maxDim = 0;
      infos.forEach((b) => {
        maxDim = Math.max(maxDim, b.size.x, b.size.y, b.size.z);
      });
      if (maxDim <= 0) maxDim = 1;

      const gap = maxDim * MULTI_MODEL_GAP_FACTOR;

      const parent = infos.map((_, i) => i);
      const find = (i) => {
        while (parent[i] !== i) {
          parent[i] = parent[parent[i]];
          i = parent[i];
        }
        return i;
      };
      const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent[rb] = ra;
      };

      if (CONNECT_SHARED_SPACE_MODELS && infos.length > 1) {
        const cMin = new THREE.Vector3(Infinity, Infinity, Infinity);
        const cMax = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
        infos.forEach((b) => {
          cMin.min(b.oc);
          cMax.max(b.oc);
        });
        const spread = Math.max(cMax.x - cMin.x, cMax.y - cMin.y, cMax.z - cMin.z);
        if (spread > maxDim * CONNECT_MIN_SPREAD_FACTOR) {
          for (let i = 1; i < infos.length; i++) union(0, i);
        }
      }

      const groupMap = new Map();
      infos.forEach((info, i) => {
        const r = find(i);
        if (!groupMap.has(r)) groupMap.set(r, []);
        groupMap.get(r).push(info);
      });

      const groups = Array.from(groupMap.values()).map((members) => {
        const min = new THREE.Vector3(Infinity, Infinity, Infinity);
        const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
        members.forEach((b) => {
          min.min(b.min);
          max.max(b.max);
        });
        return { members, min, max, width: max.x - min.x };
      });

      // For single/multi model layout - place each file at its ORIGINAL position
      // This matches what the thumbnail preview shows
      let totalWidth = 0;
      groups.forEach((g) => { totalWidth += g.width; });
      totalWidth += gap * (groups.length - 1);

      let cursorX = -totalWidth / 2;
      groups.forEach((g, gi) => {
        const cy = (g.min.y + g.max.y) / 2;
        const cz = (g.min.z + g.max.z) / 2;
        g.members.forEach((b) => {
          b.mesh.userData.groupId = gi;
          // Position preserving original relative coordinates
          b.mesh.position.set(
            cursorX + (b.oc.x - g.min.x),
            b.oc.y - cy,
            b.oc.z - cz
          );
        });
        cursorX += g.width + gap;
      });
    };

    if (isMulti) {
      let completed = 0;
      const total = fileUrls.length;

      fileUrls.forEach((fileItem, index) => {
        const url = fileItem.url || fileItem;
        const color = 0xe8e8e8;
        loadSingle(url, color, false, () => {
          completed++;
          if (completed === total && !disposed) {
            layoutMultiModels();

            const combinedBox = new THREE.Box3();
            meshesRef.current.forEach((m) => {
              m.updateMatrixWorld(true);
              const bb = new THREE.Box3().setFromObject(m);
              combinedBox.union(bb);
            });
            const combinedSize = new THREE.Vector3();
            combinedBox.getSize(combinedSize);
            const combinedCenter = new THREE.Vector3();
            combinedBox.getCenter(combinedCenter);

            radius = combinedSize.length() / 2 || 1;

            meshesRef.current.forEach((m) => {
              m.position.sub(combinedCenter);
              m.userData.homePos = m.position.clone();
              m.userData.homeQuat = m.quaternion.clone();
            });

            combinedBox.setFromCenterAndSize(new THREE.Vector3(0, 0, 0), combinedSize);

            const firstByIndex = meshesRef.current
              .slice()
              .sort((a, b) => (a.userData.fileIndex ?? 0) - (b.userData.fileIndex ?? 0))[0];

            materialRef.current = firstByIndex?.material || null;
            meshRef.current = firstByIndex || null;

            const tangentDistance = computeTangentDistance();
            fitDistance = tangentDistance * INITIAL_FIT_FACTOR;
            maxDistanceLimit = tangentDistance * ZOOM_OUT_BASE_FACTOR * MAX_ZOOM_OUT_FACTOR;

            controls.minDistance = Math.max(radius * 0.02, 0.001);
            controls.maxDistance = Math.max(radius * 20, maxDistanceLimit);

            if (!isSmall) buildMeasureVisual();
            buildClipPlaneVisual();
            if (clipPlaneGroup) clipPlaneGroup.visible = sectionActiveRef.current;

            setView("default");

            prevTime = 0;
            animate();
            setReady(true);

            if (sectionActiveRef.current) scheduleCrossSectionUpdate(50);
            if (callbacksRef.current.onLoad) callbacksRef.current.onLoad();
          }
        }, index);
      });
    } else {
      loadSingle(fileUrl, undefined, true, () => {
        if (callbacksRef.current.onLoad) callbacksRef.current.onLoad();
      });
    }

    return () => {
      disposed = true;
      if (frameId) cancelAnimationFrame(frameId);
      if (restoreTimer) clearTimeout(restoreTimer);
      if (crossSectionTimer) clearTimeout(crossSectionTimer);
      if (persistTimer) clearTimeout(persistTimer);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener("resize", handleResize);

      controls.removeEventListener("change", markDirty);
      controls.removeEventListener("change", updateClipping);
      controls.removeEventListener("start", onInteractStart);
      controls.removeEventListener("end", onInteractEnd);
      controls.removeEventListener("end", schedulePersist);
      controls.dispose();

      mount.removeEventListener("pointerdown", onPointerDown, true);
      mount.removeEventListener("contextmenu", onCanvasContextMenu, true);
      renderer.domElement.removeEventListener("contextmenu", onCanvasContextMenu);
      renderer.domElement.removeEventListener("pointermove", onPointerMoveHover);
      window.removeEventListener("pointermove", onWindowMove);
      window.removeEventListener("pointerup", onWindowUp);
      window.removeEventListener("pointercancel", onWindowUp);
      window.removeEventListener("pointermove", onMeasureMove);
      window.removeEventListener("pointerup", onMeasureUp);
      window.removeEventListener("pointercancel", onMeasureUp);
      window.removeEventListener("pointermove", onModelMove);
      window.removeEventListener("pointerup", onModelUp);
      window.removeEventListener("pointercancel", onModelUp);

      renderer.domElement.removeEventListener("pointerdown", onSectionDrawDownCapture, true);
      window.removeEventListener("pointermove", onSectionDrawMoveCapture, true);
      window.removeEventListener("pointerup", onSectionDrawUpCapture, true);
      window.removeEventListener("pointercancel", onSectionDrawUpCapture, true);

      if (drawGroup) {
        scene.remove(drawGroup);
        disposeGroup(drawGroup);
        drawGroup = null;
        drawLine = null;
        drawLineHalo = null;
        drawDots = [];
        drawDotHalos = [];
      }

      if (measureGroup) {
        if (measureGroup.parent) measureGroup.parent.remove(measureGroup);
        disposeGroup(measureGroup);
        measureGroup = null;
      }

      meshesRef.current.forEach((m) => {
        scene.remove(m);
        m.geometry.dispose();
        if (m.material) m.material.dispose();
      });
      meshesRef.current = [];
      selectedMesh = null;
      selectedGroup = [];
      modelDrag = null;

      if (clipPlaneGroup) {
        scene.remove(clipPlaneGroup);
        disposeGroup(clipPlaneGroup);
      }

      if (crossLines) {
        scene.remove(crossLines);
        crossLines.geometry.dispose();
        crossLines.material.dispose();
        crossLines = null;
      }

      materialRef.current = null;
      controlsRef.current = null;
      viewApiRef.current = null;
      meshRef.current = null;
      clipApiRef.current = null;
      measureApiRef.current = null;
      drawApiRef.current = null;
      biteApiRef.current = null;

      renderer.dispose();
      renderer.forceContextLoss();

      if (renderer.domElement && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [fileUrl, fileUrls, size, shouldLoad, isSmall, isMulti]);

  useEffect(() => {
    meshesRef.current.forEach((m) => {
      if (m.material) m.material.wireframe = wireframe;
    });
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [wireframe, ready]);

  useEffect(() => {
    const globalValue = isMulti ? 1 : opacity / 100;
    meshesRef.current.forEach((m) => {
      const mat = m.material;
      if (!mat) return;

      const idx = m.userData.fileIndex;
      const st = typeof idx === "number" ? modelStates[idx] : null;

      m.visible = st ? st.visible !== false : true;

      const own = st && typeof st.opacity === "number" ? st.opacity / 100 : 1;
      const value = globalValue * own;
      const isTransparent = value < 1;

      if (mat.transparent !== isTransparent) {
        mat.transparent = isTransparent;
        mat.depthWrite = !isTransparent;
        mat.needsUpdate = true;
      }
      mat.opacity = value;
    });
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [opacity, ready, modelStates, isMulti]);

  useEffect(() => {
    if (!ready || !isMulti) return;
    if (clipApiRef.current) clipApiRef.current.recompute(0);
  }, [modelStates, ready, isMulti]);

  useEffect(() => { autoRotateRef.current = autoRotate; }, [autoRotate]);

  useEffect(() => {
    if (!clipApiRef.current) return;
    clipApiRef.current.setEnabled(clipEnabled);
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [clipEnabled, ready]);

  useEffect(() => {
    if (!clipApiRef.current) return;
    clipApiRef.current.setSectionActive(showCrossSection);
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [showCrossSection, ready]);

  useEffect(() => {
    if (clipApiRef.current) clipApiRef.current.setInverted(clipInverted);
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [clipInverted, ready]);

  useEffect(() => {
    if (clipApiRef.current) clipApiRef.current.setOffset(planeOffset);
  }, [planeOffset, ready]);

  useEffect(() => {
    if (clipApiRef.current) clipApiRef.current.setSizeMult(planeSizeMult);
  }, [planeSizeMult, ready]);

  useEffect(() => {
    if (measureApiRef.current) measureApiRef.current.setActive(measureMode);
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [measureMode, ready]);

  useEffect(() => {
    if (!ready) {
      setBiteMode(false);
      setBiteInfo(null);
      return;
    }
    if (biteApiRef.current) biteApiRef.current.setActive(biteMode);
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [biteMode, ready]);

  useEffect(() => {
    if (isSmall) return;
    if (sectionDrawMode && measureMode) setMeasureMode(false);
    if (sectionDrawMode) {
      const el = mountRef.current?.querySelector("canvas");
      if (el) el.style.cursor = "crosshair";
    } else {
      const el = mountRef.current?.querySelector("canvas");
      if (el) el.style.cursor = "default";
    }
    if (viewApiRef.current) viewApiRef.current.requestRender();
  }, [sectionDrawMode, isSmall, measureMode]);

  useEffect(() => {
    if (!ready || isSmall) return;
    try {
      viewApiRef.current?.persistState?.();
    } catch {
      /* ignore */
    }
  }, [
    ready, wireframe, autoRotate, opacity, showOpacity, expanded,
    sidebarHidden, showCrossSection, clipEnabled, clipInverted,
    planeOffset, planeSizeMult, measureMode, isSmall,
  ]);

  const goTo = (name) => viewApiRef.current && viewApiRef.current.setView(name);
  const toggleSidebar = () => setExpanded(!expanded);
  const toggleBite = () => setBiteMode((v) => !v);

  // ✅ NEW: take a screenshot of the current 3D view (kept in memory only)
  // area = { x, y, w, h } in container pixels, or null for the whole view
  const takeScreenshot = (area = null) => {
    const api = viewApiRef.current;
    if (!api || !api.captureCanvas) return;

    let canvas = null;
    try {
      canvas = api.captureCanvas(getViewerBackground(containerRef.current));
    } catch (err) {
      console.error("STLViewer: screenshot failed", err);
      return;
    }
    if (!canvas) return;

    drawMeasureLabelOnto(canvas, measureLabelRef.current, mountRef.current);

    // crop to the selected area
    if (area && containerRef.current && mountRef.current) {
      const gl = mountRef.current.querySelector("canvas");
      if (gl) {
        const gr = gl.getBoundingClientRect();
        const cr = containerRef.current.getBoundingClientRect();
        const s = canvas.width / Math.max(gr.width, 1);

        let sx = Math.round((area.x + cr.left - gr.left) * s);
        let sy = Math.round((area.y + cr.top - gr.top) * s);
        let sw = Math.round(area.w * s);
        let sh = Math.round(area.h * s);

        sx = Math.max(0, Math.min(sx, canvas.width - 1));
        sy = Math.max(0, Math.min(sy, canvas.height - 1));
        sw = Math.max(1, Math.min(sw, canvas.width - sx));
        sh = Math.max(1, Math.min(sh, canvas.height - sy));

        const cropped = document.createElement("canvas");
        cropped.width = sw;
        cropped.height = sh;
        cropped.getContext("2d").drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
        canvas = cropped;
      }
    }

    let url = null;
    try {
      url = canvas.toDataURL("image/png");
    } catch (err) {
      console.error("STLViewer: screenshot export failed", err);
      return;
    }

    setShotFlash((n) => n + 1);
    setShotCanNote(canAddScreenshotNote());
    setShot(url);
  };

  // ✅ NEW: screenshot button -> pick an area (drag) or the whole view
  const startCapture = () => {
    if (!ready || shot) return;
    setCaptureSel(null);
    captureDragRef.current = null;
    setCaptureMode(true);
  };

  const cancelCapture = () => {
    setCaptureMode(false);
    setCaptureSel(null);
    captureDragRef.current = null;
  };

  const captureWhole = () => {
    cancelCapture();
    takeScreenshot(null);
  };

  const containerPoint = (ev) => {
    const r = containerRef.current.getBoundingClientRect();
    return {
      x: Math.min(Math.max(ev.clientX - r.left, 0), r.width),
      y: Math.min(Math.max(ev.clientY - r.top, 0), r.height),
    };
  };

  const onCapturePointerDown = (ev) => {
    if (ev.button !== undefined && ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    const p = containerPoint(ev);
    captureDragRef.current = p;
    setCaptureSel({ x: p.x, y: p.y, w: 0, h: 0 });
    try {
      ev.currentTarget.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onCapturePointerMove = (ev) => {
    const start = captureDragRef.current;
    if (!start) return;
    const p = containerPoint(ev);
    setCaptureSel({
      x: Math.min(start.x, p.x),
      y: Math.min(start.y, p.y),
      w: Math.abs(p.x - start.x),
      h: Math.abs(p.y - start.y),
    });
  };

  const onCapturePointerUp = () => {
    if (!captureDragRef.current) return;
    captureDragRef.current = null;
    const sel = captureSel;
    if (!sel || sel.w < 12 || sel.h < 12) {
      setCaptureSel(null); // too small - treat as a click, keep choosing
      return;
    }
    setCaptureMode(false);
    setCaptureSel(null);
    takeScreenshot(sel);
  };

  // Esc while choosing = cancel (and don't close the viewer modal behind)
  useEffect(() => {
    if (!captureMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.stopPropagation();
        cancelCapture();
      } else if (e.key === "Enter") {
        e.preventDefault();
        captureWhole();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode]);

  // ✅ NEW: header button (outside the viewer) -> start choosing the area
  const startCaptureRef = useRef(startCapture);
  startCaptureRef.current = startCapture;

  useEffect(() => {
    if (isSmall || !ready) return undefined;
    const onRequest = (e) => {
      e.preventDefault(); // handled
      startCaptureRef.current();
    };
    window.addEventListener(STL_CAPTURE_REQUEST_EVENT, onRequest);
    return () => window.removeEventListener(STL_CAPTURE_REQUEST_EVENT, onRequest);
  }, [isSmall, ready]);

  const closeScreenshot = () => setShot(null);

  const handleScreenshotToNote = (file) => {
    const ok = sendScreenshotToNote(file);
    if (ok) setShot(null);
    return ok;
  };

  const openCrossSection = () => {
    setSectionDrawMode(true);
    setShowCrossSection(false);
  };

  const closeCrossSection = () => {
    setShowCrossSection(false);
    setSidebarHidden(false);
    setClipEnabled(false);
    setSectionDrawMode(false);
  };

  const handleSectionButton = () => {
    if (showCrossSection) closeCrossSection();
    else if (sectionDrawMode) {
      setSectionDrawMode(false);
      setSidebarHidden(false);
    } else openCrossSection();
  };

  const clearSection = () => {
    setShowCrossSection(false);
    setSidebarHidden(false);
    setClipEnabled(false);
    setSectionDrawMode(true);
    sectionDrawModeRef.current = true;
    setPlaneOffset(0);
    if (clipApiRef.current) clipApiRef.current.resetOrientation();
  };

  const clearMeasurements = () => {
    if (measureApiRef.current) measureApiRef.current.clear();
    setXsMeasureVersion((v) => v + 1);
  };

  const reset2DView = () => {
    if (xsViewRef.current) {
      xsViewRef.current.zoom = 1;
      xsViewRef.current.panX = 0;
      xsViewRef.current.panY = 0;
    }
    setXsMeasureVersion((v) => v + 1);
  };

  const zoom2D = (factor) => {
    if (xsViewRef.current) {
      xsViewRef.current.zoom = Math.max(0.1, Math.min(50, xsViewRef.current.zoom * factor));
    }
    setXsMeasureVersion((x) => x + 1);
  };

  const onDockPointerDown = (ev) => {
    if (ev.button !== 2) return;
    const el = dockRef.current;
    const host = containerRef.current;
    if (!el || !host) return;
    ev.preventDefault();
    ev.stopPropagation();

    const sx = ev.clientX;
    const sy = ev.clientY;
    setDockDragging(true);

    const move = (e) => {
      el.style.transform =
        "translate(" + (e.clientX - sx) + "px, " + (e.clientY - sy) + "px)";
    };
    const up = (e) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      el.style.transform = "";
      const { edge, pos } = snapDock(host, e.clientX, e.clientY);
      setDockEdge(edge);
      setDockPos(pos);
      saveLayout({ dockEdge: edge, dockPos: pos });
      setDockDragging(false);
      swallowNextContextMenu();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  const onXsTitlePointerDown = (ev) => {
    if (ev.button !== 2) return;
    const el = xsViewerRef.current;
    const host = containerRef.current;
    if (!el || !host) return;
    ev.preventDefault();
    ev.stopPropagation();

    const hr = host.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const startL = r.left - hr.left;
    const startT = r.top - hr.top;
    const sx = ev.clientX;
    const sy = ev.clientY;
    let curL = startL;
    let curT = startT;

    setXsPos({ fx: startL / hr.width, fy: startT / hr.height });

    const move = (e) => {
      curL = Math.min(Math.max(0, startL + e.clientX - sx), Math.max(0, hr.width - r.width));
      curT = Math.min(Math.max(0, startT + e.clientY - sy), Math.max(0, hr.height - r.height));
      el.style.left = curL + "px";
      el.style.top = curT + "px";
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      const next = { fx: curL / hr.width, fy: curT / hr.height };
      setXsPos(next);
      saveLayout({ xsPos: next });
      swallowNextContextMenu();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  const resetXsPos = () => {
    setXsPos(null);
    saveLayout({ xsPos: null });
  };

  if (isSmall) {
    return <div ref={mountRef} className="stl-viewer-small" />;
  }

  const linkable = {};
  if (isMulti && ready) {
    const cnt = {};
    meshesRef.current.forEach((m) => {
      const g = m.userData.groupId;
      if (g !== undefined) cnt[g] = (cnt[g] || 0) + 1;
    });
    meshesRef.current.forEach((m) => {
      const g = m.userData.groupId;
      if (g !== undefined && cnt[g] > 1) linkable[m.userData.fileIndex] = true;
    });
  }

  const hasActiveDisplay = wireframe || autoRotate || opacity < OPACITY_MAX || biteMode;

  const dockHorizontal = dockEdge === "top" || dockEdge === "bottom";
  const dockClass =
    "stl-right-dock" +
    " edge-" + dockEdge +
    " pos-" + dockPos +
    (dockHorizontal ? " horizontal" : "") +
    (dockDragging ? " dragging" : "") +
    (sidebarHidden ? " hidden" : "");

  return (
    <div ref={containerRef} className="stl-viewer-container">
      <div ref={mountRef} className="stl-viewer-mount" />

      {ready && (
        <React.Fragment>
          {sectionDrawMode && !showCrossSection && <DrawHint key="draw-hint" />}

          {/* ✅ NEW: screenshot (icon only) + area selection */}
          <style key="shot-css">{SHOT_BTN_CSS}</style>
          {showScreenshotButton && !captureMode && (
            <button
              key="shot-btn"
              type="button"
              className={`stl-shot-btn${sectionDrawMode && !showCrossSection ? " is-low" : ""}`}
              onClick={startCapture}
              title="Screenshot - select an area or take the whole view"
              aria-label="Take screenshot"
            >
              <CameraIcon size={18} />
            </button>
          )}

          {captureMode && (
            <div
              key="cap-overlay"
              className={`stl-cap-overlay${captureSel && captureSel.w > 0 ? " has-sel" : ""}`}
              onPointerDown={onCapturePointerDown}
              onPointerMove={onCapturePointerMove}
              onPointerUp={onCapturePointerUp}
              onPointerCancel={onCapturePointerUp}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div
                className="stl-cap-bar"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <span className="stl-cap-hint">
                  <CameraIcon size={14} />
                  <span>
                    Drag to select an area
                    <span className="stl-cap-hint-long"> · or capture the whole view</span>
                  </span>
                </span>
                <button type="button" className="stl-cap-btn primary" onClick={captureWhole}>
                  Full view
                </button>
                <button type="button" className="stl-cap-btn ghost" onClick={cancelCapture}>
                  Cancel
                </button>
              </div>

              {captureSel && captureSel.w > 0 && (
                <div
                  className="stl-cap-sel"
                  style={{
                    left: captureSel.x,
                    top: captureSel.y,
                    width: captureSel.w,
                    height: captureSel.h,
                  }}
                >
                  {captureSel.w >= 12 && captureSel.h >= 12 && (
                    <span
                      className="stl-cap-size"
                      style={captureSel.y < 30 ? { top: 4, left: 4 } : undefined}
                    >
                      {Math.round(captureSel.w)} × {Math.round(captureSel.h)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {shotFlash > 0 && <div key={`shot-flash-${shotFlash}`} className="stl-shot-flash" />}

          <div
            key="measure-label"
            ref={measureLabelRef}
            className="stl-measure-label"
            style={{
              position: "absolute",
              left: 0, top: 0,
              display: "none",
              zIndex: 11,
              pointerEvents: "none",
              padding: "5px 12px",
              borderRadius: 8,
              background: "linear-gradient(135deg, rgba(33, 150, 243, 0.95) 0%, rgba(25, 118, 210, 0.95) 100%)",
              border: "1px solid rgba(100, 181, 246, 0.6)",
              boxShadow: "0 4px 16px rgba(33, 150, 243, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)",
              color: "#ffffff",
              fontFamily: '"SF Mono", "Consolas", "Monaco", monospace',
              fontSize: 13, fontWeight: 700,
              letterSpacing: "0.03em",
              whiteSpace: "nowrap",
              willChange: "transform",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
            }}
          />

          {showCrossSection && (
            <CrossSectionPanel
              key="xs-panel"
              planeOffset={planeOffset}
              setPlaneOffset={setPlaneOffset}
              planeSizeMult={planeSizeMult}
              setPlaneSizeMult={setPlaneSizeMult}
              zoom2D={zoom2D}
              reset2DView={reset2DView}
              clearSection={clearSection}
              clearMeasurements={clearMeasurements}
              clipEnabled={clipEnabled}
              setClipEnabled={setClipEnabled}
              clipInverted={clipInverted}
              setClipInverted={setClipInverted}
              closeCrossSection={closeCrossSection}
              clipApiRef={clipApiRef}
            />
          )}

          {showCrossSection && (
            <CrossSectionViewer2D
              key="xs-viewer"
              xsViewerRef={xsViewerRef}
              xsPos={xsPos}
              xsScale={xsScale}
              setXsScale={setXsScale}
              changeXsScale={changeXsScale}
              resetXsPos={resetXsPos}
              onXsTitlePointerDown={onXsTitlePointerDown}
              crossSectionCanvasRef={crossSectionCanvasRef}
              crossSectionOutline={crossSectionOutline}
            />
          )}

          {showOpacity && !isMulti && (
            <OpacityPanel key="opacity-panel" opacity={opacity} setOpacity={setOpacity} />
          )}

          {isMulti && showOpacity && (
            <FilesPanel
              key="files-panel"
              fileUrls={fileUrls}
              filesPanelOpen={filesPanelOpen}
              setFilesPanelOpen={setFilesPanelOpen}
              showAllModels={showAllModels}
              getModelState={getModelState}
              updateModelState={updateModelState}
              linkable={linkable}
              toggleModelDetached={toggleModelDetached}
            />
          )}

          <Sidebar
            key="sidebar"
            goTo={goTo}
            wireframe={wireframe}
            setWireframe={setWireframe}
            showOpacity={showOpacity}
            setShowOpacity={setShowOpacity}
            autoRotate={autoRotate}
            setAutoRotate={setAutoRotate}
            showCrossSection={showCrossSection}
            sectionDrawMode={sectionDrawMode}
            handleSectionButton={handleSectionButton}
            measureMode={measureMode}
            setMeasureMode={setMeasureMode}
            expanded={expanded}
            toggleSidebar={toggleSidebar}
            hasActiveDisplay={hasActiveDisplay}
            opacity={opacity}
            dockClass={dockClass}
            dockRef={dockRef}
            onDockPointerDown={onDockPointerDown}
            canBite={isMulti && ready}
            biteMode={biteMode}
            toggleBite={toggleBite}
          />

          {biteMode && biteInfo && (
            <div
              key="bite-legend"
              style={{
                position: "absolute",
                left: "50%",
                bottom: 12,
                transform: "translateX(-50%)",
                zIndex: 11,
                pointerEvents: "none",
                padding: "8px 14px",
                borderRadius: 8,
                background: "rgba(15, 23, 42, 0.88)",
                color: "#fff",
                fontSize: 12,
                lineHeight: 1.5,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Bite contact map</div>
              <div
                style={{
                  height: 8,
                  borderRadius: 4,
                  background:
                    "linear-gradient(90deg, hsl(0 90% 50%), hsl(60 90% 50%), hsl(120 90% 50%), hsl(180 90% 50%), hsl(238 90% 50%))",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", opacity: 0.85 }}>
                <span>0 {MEASURE_UNIT} (contact)</span>
                <span>{BITE_MAX_MM} {MEASURE_UNIT}+</span>
              </div>
              <div style={{ marginTop: 4 }}>
                Closed by {formatMm(Math.abs(biteInfo.closed))} {MEASURE_UNIT} · Contact area ≈{" "}
                {biteInfo.area.toFixed(1)} {MEASURE_UNIT}²
              </div>
            </div>
          )}

          {showCrossSection && sidebarHidden && (
            <button
              key="hamburger"
              type="button"
              className="stl-hamburger"
              onClick={() => setSidebarHidden(false)}
              title="Show controls"
              aria-label="Show controls"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          )}

          <HelpOverlay
            key="help-overlay"
            sectionDrawMode={sectionDrawMode}
            isMulti={isMulti}
            measureMode={measureMode}
            showCrossSection={showCrossSection}
          />
        </React.Fragment>
      )}

      {/* ✅ NEW: screenshot editor (draw + Add Note) */}
      {shot && (
        <ScreenshotEditor
          image={shot}
          canAddNote={shotCanNote}
          onClose={closeScreenshot}
          onAddNote={handleScreenshotToNote}
        />
      )}
    </div>
  );
};

export default STLViewer;